// SignupOwner.js
import {
  getDatabase,
  ref,
  push,
  set,
  update,
  get,
  remove,
  onValue,
  off,
} from "firebase/database";

import React, { useState, useEffect, useCallback } from "react";
import { useFormContext } from "../contexts/FormContext";
import { useAuth } from "../contexts/AuthContext";
import { validatePricingFields, hasPricingErrors } from "./utils/formValidation";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import "react-phone-input-2/lib/style.css";
import { Button, Form, ProgressBar, Row, Col, Modal, InputGroup, Spinner, ButtonGroup, ToggleButton } from "react-bootstrap";
import {
  getListingCharterOptions,
  getListingFeaturesOptions,
  updateListingStatusById,
  getCurrentUserDetails,
  newListingAddedAdminEmailBody,
  sendEmail,
	  getUserByID,
	  getListingLockDatesByID,
	  getFullDayLockedDates,
	} from "./utils";
import { notifyNewBoat, notifyListingModified, getBoatStatusAfterOwnerEdit } from "./utils/adminNotifications";
import { notifyOwnerBoatAddedByAdmin } from "./utils/userNotifications";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import Alert from "@mui/material/Alert";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRange } from "react-date-range";
import { addDays } from "date-fns";
import CreateListingDataTable from "./CreateListingDataTable";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';

const PREDEFINED_FISHING_STYLES = ["Inshore", "Nearshore", "Offshore / Deep Sea", "Reef / Wreck", "Flats Fishing", "Backcountry", "River / Lake / Reservoir", "Night Fishing", "Ice Fishing"];
const PREDEFINED_TARGET_SPECIES = ["Redfish", "Snook", "Trout", "Tarpon", "Bass", "Crappie", "Snapper", "Grouper", "Mahi Mahi", "Tuna", "Sailfish", "Marlin", "Shark"];
const PREDEFINED_TECHNIQUES = ["Bottom Fishing", "Trolling", "Jigging", "Spinning", "Popping", "Drift Fishing", "Fly Fishing", "Spearfishing", "Handline"];
const PREDEFINED_INCLUDED_ITEMS = ["Rods, Reels & Tackle", "Lures", "Live Bait", "Fishing Licenses", "Catch Cleaning & Filleting", "Drinks", "Snacks", "Lunch", "Ice & Cooler", "Fuel Included", "Insurance Included"];
const PREDEFINED_EXCLUDED_ITEMS = ["Fuel Surcharge", "Cleaning Fee"];

function SortableItem(props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: '10px 15px',
    marginBottom: '8px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    cursor: 'grab',
    touchAction: 'none' // Prevent scrolling while dragging on touch devices
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <span style={{ fontSize: '14px', fontWeight: '500' }}>{props.id}</span>
      <i className="fas fa-grip-lines" style={{ color: '#ccc' }}></i>
    </div>
  );
}

const CreateListing = ({ initialData, targetOwnerId }) => {
  const today = dayjs(); // Use dayjs to get today's date
  const serverUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { setIsFormDirty, setFormType } = useFormContext();

  const [step, setStep] = useState(1);
  const [listingLockDates, setListingLockDates] = useState([]);

  const [pricing, setPricing] = useState({
    hours: 1,
    hourlyRate: 0,
    maxPassengers: 0,
    additionalPassengersAllowed: 0,
    additionalPassengerPercentage: 0,
    additionalPassengerPriceType: "percentage",
    overnightEnabled: true,
    hourlyOptions: [],
    dailyOptions: [],
    tripsStartingFrom: "",
  });

  const { overnightEnabled } = pricing; // Destructuring to get overnightEnabled from pricing state

  const toggleOvernight = () => {
    setPricing((prevState) => ({
      ...prevState,
      overnightEnabled: !prevState.overnightEnabled, // Toggle the value based on previous state
    }));
  };

  const { signup } = useAuth();
  const [error, setError] = useState("");
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("Pending");
  const [ownerName, setOwnerName] = useState("-");

  const [selectedValue, setSelectedValue] = useState(false);
  const [pickupAvailable, setPickupAvailable] = useState(false);
  const [lockStartDate, setLockStartDate] = useState("");
  const [lockEndDate, setLockEndDate] = useState("");
  const [timeRanges, setTimeRanges] = useState([]);
  const [currentStartTime, setCurrentStartTime] = useState(null);
  const [currentEndTime, setCurrentEndTime] = useState(null);
  const [selectionRange, setSelectedRange] = useState([
    {
      startDate: moment().tz("UTC").startOf("day").add(1, "day").toDate(),
      endDate: moment().tz("UTC").startOf("day").add(1, "day").toDate(),
      key: "selection",
    },
  ]);

  // Custom Options State
  const [showCustomStyleInput, setShowCustomStyleInput] = useState(false);
  const [newStyle, setNewStyle] = useState("");

  const [showCustomSpeciesInput, setShowCustomSpeciesInput] = useState(false);
  const [newSpecies, setNewSpecies] = useState("");

  const [showCustomTechniqueInput, setShowCustomTechniqueInput] = useState(false);
  const [newTechnique, setNewTechnique] = useState("");

  const [showCustomIncludedInput, setShowCustomIncludedInput] = useState(false);
  const [newIncludedItem, setNewIncludedItem] = useState("");
  const [showCustomExcludedInput, setShowCustomExcludedInput] = useState(false);
  const [newExcludedItem, setNewExcludedItem] = useState("");

  const handleAddCustomIncludedOption = (value, setter, visibilitySetter) => {
    if (!value || value.trim() === "") return;

    const currentOptions = formData.fishingGuideDetails.whatsIncluded.included || [];
    if (!currentOptions.includes(value.trim())) {
      handleNestedChange(`fishingGuideDetails.whatsIncluded.included`, [...currentOptions, value.trim()]);
    }

    setter("");
    visibilitySetter(false);
  };

  const handleAddCustomExcludedOption = (value, setter, visibilitySetter) => {
    if (!value || value.trim() === "") return;

    const currentOptions = formData.fishingGuideDetails.whatsIncluded.notIncluded.excluded || [];
    if (!currentOptions.includes(value.trim())) {
      handleNestedChange(`fishingGuideDetails.whatsIncluded.notIncluded.excluded`, [...currentOptions, value.trim()]);
    }

    setter("");
    visibilitySetter(false);
  };

  const handleAddCustomOption = (category, value, setter, visibilitySetter, fieldPath) => {
    if (!value || value.trim() === "") return;

    // Check if it's already in the list (standard or custom)
    const currentOptions = formData.fishingGuideDetails.fishingExperience[category] || [];
    if (!currentOptions.includes(value.trim())) {
      handleNestedChange(`fishingGuideDetails.fishingExperience.${fieldPath}`, [...currentOptions, value.trim()]);
    }

    setter("");
    visibilitySetter(false);
  };

  const [boatTypes, setBoatTypes] = useState([]);
  useEffect(() => {
    const database = getDatabase();
    const boatTypesRef = ref(database, "portalSettings/settingsID/boatTypes");

    const handleData = (snapshot) => {
      if (snapshot.exists()) {
        const boatTypesData = Object.entries(snapshot.val()).map(
          ([id, value]) => ({
            id,
            value,
          })
        );
        setBoatTypes(boatTypesData);
      } else {
        console.log("No data found");
        setBoatTypes([]);
      }
    };

    onValue(boatTypesRef, handleData);

    // Clean up the listener when the component unmounts
    return () => {
      // Detach the event listener
      off(boatTypesRef, "value", handleData);
    };
  }, []);

  const handleHourlyOptionChange = (index, event) => {
    const { name, value } = event.target;
    const newOptions = [...pricing.hourlyOptions];
    newOptions[index][name] = value;
    setPricing({ ...pricing, hourlyOptions: newOptions });
  };

  const handleDailyOptionChange = (index, event) => {
    const { name, value } = event.target;
    const newOptions = [...pricing.dailyOptions];
    newOptions[index][name] = value;
    setPricing({ ...pricing, dailyOptions: newOptions });
  };

  const addHourlyOption = () => {
    setPricing({
      ...pricing,
      hourlyOptions: [...pricing.hourlyOptions, {
        hours: "",
        price: "",
        maxPassengers: 0,
        additionalPassengersAllowed: 0,
        additionalPassengerPercentage: 0,
        additionalPassengerPriceType: "percentage",
      }],
    });
  };

  const addDailyOption = () => {
    setPricing({
      ...pricing,
      dailyOptions: [...pricing.dailyOptions, { days: "", price: "" }],
    });
  };

  const removeHourlyOption = (index) => {
    const newOptions = [...pricing.hourlyOptions];
    newOptions.splice(index, 1);
    setPricing({ ...pricing, hourlyOptions: newOptions });
  };

  const removeDailyOption = (index) => {
    const newOptions = [...pricing.dailyOptions];
    newOptions.splice(index, 1);
    setPricing({ ...pricing, dailyOptions: newOptions });
  };

  const isDateDisabled = (date) => {
    const formattedDate = moment.tz(date, "UTC").format("YYYY-MM-DD");
    return getFullDayLockedDates(listingLockDates, "YYYY-MM-DD").includes(formattedDate);
  };

  const handleSelectedRange = (value) => {
    setSelectedRange(value);
  };

  const handleDateClick = (val) => {
    if (isDateDisabled(val)) {
      console.log("Disabled date clicked:", val);
      // Add your custom logic here
    } else {
      console.log("click");
    }
  };

  const [unlockStartDateRange, setUnlockStartDateRange] = useState(dayjs());
  const [unlockEndDateRange, setUnlockEndDateRange] = useState(dayjs());

  useEffect(() => {
    const startDate = selectionRange[0].startDate;

    const year = startDate.getFullYear();
    const month = String(startDate.getMonth() + 1).padStart(2, "0"); // Adding 1 to the month since months are zero-indexed
    const day = String(startDate.getDate()).padStart(2, "0");
    const formattedStartDate = `${year}-${month}-${day}`;
    setLockStartDate(formattedStartDate);
    console.log(formattedStartDate);

    const endDate = selectionRange[0].endDate;

    const yearEnd = endDate.getFullYear();
    const monthEnd = String(endDate.getMonth() + 1).padStart(2, "0"); // Adding 1 to the month since months are zero-indexed
    const dayEnd = String(endDate.getDate()).padStart(2, "0");
    const formattedEndDate = `${yearEnd}-${monthEnd}-${dayEnd}`;
    setLockEndDate(formattedEndDate);
    console.log("EndDate");
    console.log(formattedEndDate);
  }, [selectionRange]);

  const [disabledDates, setDisabledDates] = useState([]);

  useEffect(() => {
    const disabledDatesUTC = getFullDayLockedDates(listingLockDates, "YYYY-MM-DD");

    // Convert to local dates without UTC conversion
    const disabledDatesLocal = disabledDatesUTC.map((date) =>
      moment(date).startOf("day").toDate()
    );

    setDisabledDates(disabledDatesLocal);
    console.log("Final Disabled Dates (Local Format):", disabledDatesLocal);
  }, [listingLockDates]);

  useEffect(() => {
    const fetchLockDates = async () => {
      try {
        const lockDates = await getListingLockDatesByID(initialData.id);
        console.log("Raw lockDates from Firebase:", lockDates);

        const lockDatesArray = Object.entries(lockDates).map(([id, date]) => ({
          id,
          // Add 1 day to both start and end dates to correct the off-by-one error
          start_date: new Date(date.start_date),
          end_date: new Date(date.end_date),
          time_ranges: date.time_ranges || [],
          start_time: date.start_time || null,
          end_time: date.end_time || null,
        }));

        setListingLockDates(lockDatesArray);
        console.log("Processed lockDatesArray:", lockDatesArray);
      } catch (error) {
        console.error("Error fetching lock dates:", error);
      }
    };

    if (initialData?.id) {
      fetchLockDates();
    }
  }, [initialData]);

  function getDatesInRange(startDate, endDate) {
    const dates = [];
    let currentDate = moment(startDate).startOf("day"); // Remove UTC conversion
    const end = moment(endDate).startOf("day");

    while (currentDate.isSameOrBefore(end, "day")) {
      dates.push(currentDate.format()); // Store as ISO string
      currentDate.add(1, "day");
    }

    return dates;
  }

  const [formData, setFormData] = useState({
    listingTitle: "",
    shortName: "",
    capacity: "",
    boatType: "",
    description: "",
    liabilityCoverageAcknowledged: false,
    ownerId: auth.currentUser.uid,
    featured_image: null,
    storageAddress: "",
    make: "",
    model: "",
    year: "",
    hin: "",
    motorMake: "",
    motorModel: "",
    motorYear: "",
    motorSerial: "",
    trailerMake: "",
    trailerAxle: "",
    images: "",
    trailerShocks: "",
    trailerYear: "",
    trailerVin: "",
    trailerSpareWheel: "",
    trailerTireLegal: "",
    trollingMotorMake: "",
    trollingMotorModel: "",
    trollingMotorYear: "",
    trollingSpareTmProp: "",
    trollingBigMotorProp: "",
    shallowWaterAnchors: "",
    shallowWaterAnchorBrandModel: "",
    ffsMake: "",
    ffsModel: "",
    ffsYear: "",
    graph1Make: "",
    graph1Model: "",
    graph1Year: "",
    graph2Make: "",
    graph2Model: "",
    graph2Year: "",
    graph3Make: "",
    graph3Model: "",
    graph3Year: "",
    graph4Make: "",
    graph4Model: "",
    graph4Year: "",
    insuranceProvider: "",
    insuranceName: "",
    insuranceNumber: "",
    insuranceCoverage: "",
    boatCaptain: false,
    numberOfBoats: 1,
    fishingGuideDetails: {
      primaryContact: {
        fullName: "",
        role: "",
        email: "",
        mobile: "",
        alternatePhone: "",
      },
      businessInfo: {
        name: "",
        type: "",
        country: "",
        state: "",
        city: "",
        website: "",
      },
      listingType: "",
      listingTitle: "", // Public Listing Title
      tagline: "",
      fishingExperience: {
        styles: [],
        species: [],
        techniques: [],
      },
      whatsIncluded: {
        included: [],
        notIncluded: {
          excluded: [],
          fuelSurcharge: "",
          cleaningFee: "",
          gratuityExpected: false,
          recommendedGratuity: "",
          additionalInfo: "",
        },
      },
      legal: {
        captain: {
          fullName: "",
          licenseNumber: ""
        },
        licenses: {
          sixPackLicense: false,
          masterCaptainLicense: false,
          coastGuardCOI: false,
          fishingCharterInsurance: false,
          licenseDocuments: []
        }
      },
      tripPackages: [
        {
          name: "",
          duration: "",
          startTimeOptions: [],
          basePrice: "",
          maxGuests: "",
          priceChangeOptions: {
            type: "Flat Trip Price Only",
            extraPersonPrice: "",
            afterGuests: "",
          },
          daysOffered: {
            Mon: false,
            Tue: false,
            Wed: false,
            Thu: false,
            Fri: false,
            Sat: false,
            Sun: false,
          },
          seasonalAvailability: "",
          description: "",
          instantBook: false,
          freeCancellation: false,
          availableDateRange: {
            startDate: "",
            endDate: "",
          },
        },
      ],
      calendarRules: {
        leadTime: "",
        noticeRequired: "",
        instantBook: false,
        recurringDaysOff: [],
        blockedDates: [],
      },
      policies: {
        cancellation: "",
        arrivalLate: {
          requiredArrivalTime: "",
          latePolicy: "",
        },
        paymentTerms: {
          depositPercentage: "",
          balanceDue: "",
          acceptedPaymentOnDock: [],
          collectBalanceAtMeetup: false,
        },
      },
      legal: {
        captain: {
          fullName: "",
          licenseType: "",
          licenseNumber: "",
          licenseExpiry: "",
          licenseFile: null,
        },
        boatRegistration: {
          number: "",
          authority: "",
          expiry: "",
          registrationFile: null,
        },
        fishingLicenses: {
          type: "",
          number: "",
          expiry: "",
          licenseFile: null,
        },
        insurance: {
          provider: "",
          policyNumber: "",
          coverageType: "",
          expiry: "",
          insuranceFile: null,
        },
      },
      media: {
        promoVideoUrl: "",
      },
      houseRules: {
        onboardRules: [],
        catchPolicy: "",
        specialRequirements: "",
      },
      messaging: {
        welcomeMessage: "",
        preTripReminder: "",
      },
    },
  });

  const [charterOptions, setCharterOptions] = useState([]);
  const [listingFeatures, setListingFeatures] = useState([]);

  const initialCheckboxes = listingFeatures.map((feature, index) => ({
    id: `checkbox${index + 1}`,
    label: feature,
    checked: false,
  }));

  const [checkboxes, setCheckboxes] = useState(initialCheckboxes);

  const [userDetails, setUserDetails] = useState([]);

  const [showLockDatesModal, setLockDatesModal] = useState(false);

  const openLockDatesModal = () => {
    setLockDatesModal(true);
  };

  const closeLockDatesModal = () => {
    setLockDatesModal(false);
    setTimeRanges([]);
    setCurrentStartTime(null);
    setCurrentEndTime(null);
  };

  // Functions to manage time ranges
  const addTimeRange = () => {
    if (currentStartTime && currentEndTime) {
      const start = currentStartTime.format('HH:mm');
      const end = currentEndTime.format('HH:mm');
      const startMoment = moment(start, 'HH:mm');
      const endMoment = moment(end, 'HH:mm');

      if (!endMoment.isAfter(startMoment)) {
        setError("End time must be after start time.");
        return;
      }

      const hasOverlap = timeRanges.some((range) => {
        const existingStart = moment(range.start, 'HH:mm');
        const existingEnd = moment(range.end, 'HH:mm');
        return startMoment.isBefore(existingEnd) && endMoment.isAfter(existingStart);
      });

      if (hasOverlap) {
        setError("Time ranges cannot overlap.");
        return;
      }

      const newRange = {
        id: Date.now(), // Simple unique ID
        start,
        end
      };
      setTimeRanges([...timeRanges, newRange]);
      setCurrentStartTime(null);
      setCurrentEndTime(null);
      setError("");
    }
  };

  const removeTimeRange = (id) => {
    setTimeRanges(timeRanges.filter(range => range.id !== id));
  };

  const clearTimeRanges = () => {
    setTimeRanges([]);
    setCurrentStartTime(null);
    setCurrentEndTime(null);
  };

  const [priorityFeatures, setPriorityFeatures] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setPriorityFeatures((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Sync priority features with selected checkboxes
  useEffect(() => {
    const checkedFeatures = checkboxes.filter(c => c.checked).map(c => c.label);

    setPriorityFeatures(prev => {
      // Filter out any that are no longer checked
      const filtered = prev.filter(f => checkedFeatures.includes(f));

      // Add any new checked ones that aren't in the list yet
      const newones = checkedFeatures.filter(f => !prev.includes(f));

      return [...filtered, ...newones];
    });
  }, [checkboxes]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const charterOptions = await getListingCharterOptions();
        const charterOptionsArray = Object.values(charterOptions);
        setCharterOptions(charterOptionsArray);

        const listingFeatures = await getListingFeaturesOptions();
        const listingFeaturesArray = Object.values(listingFeatures);
        setListingFeatures(listingFeaturesArray);

        const userData = await getCurrentUserDetails();
        setUserDetails(userData);

        const ownerIdToFetch =
          initialData?.ownerId || targetOwnerId || auth.currentUser?.uid;
        const ownerData = await getUserByID(ownerIdToFetch);
        console.log("Owner data from getUserByID:", ownerData);
        setOwnerName(`${ownerData.firstName} ${ownerData.lastName}`);

        console.log("listingFeaturesArray");
        console.log(listingFeaturesArray);

        if (initialData && initialData.features != null) {
          const processedFeatures = Array.isArray(initialData.features)
            ? initialData.features
            : Object.values(initialData.features);

          setSelectedCheckboxes(processedFeatures);

          if (initialData.priorityFeatures) {
            setPriorityFeatures(initialData.priorityFeatures);
          }

          // Append values from initialFeatures that are not already present in listingFeaturesArray
          processedFeatures.forEach((feature) => {
            if (
              !listingFeaturesArray.some(
                (checkbox) => (checkbox.label || checkbox) == feature
              )
            ) {
              const newCheckbox = {
                id: `checkbox${listingFeaturesArray.length + 1}`,
                label: feature.toString(),
                checked: false, // You might want to set this to the default value
              };
              listingFeaturesArray.push(newCheckbox);
            }
          });
        }
        const initialCheckboxes = listingFeaturesArray.map((feature, index) => {
          let isChecked;
          const featureLabel =
            typeof feature === "object" ? feature.label : feature;
          if (initialData && initialData.features != null) {
            const featuresList = Array.isArray(initialData.features)
              ? initialData.features
              : Object.values(initialData.features);
            isChecked = featuresList.includes(featureLabel);
          } else {
            isChecked = false;
          }

          return {
            id: `checkbox${index + 1}`,
            label: featureLabel,
            checked: isChecked,
          };
        });
        setCheckboxes(initialCheckboxes);
      } catch (error) {
        console.error("Error fetching listingFeaturesArray:", error);
      }
    };

    fetchQuestions();
  }, [initialData]);

  const getLocationName = async (lat, lng) => {
    try {
      if (!lat || !lng) return "Location not set";

      const dropoffLat = lat;
      const dropoffLlng = lng;

      const dropOffResponse = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${dropoffLat},${dropoffLlng}&key=AIzaSyBfEcoyW9DM7QQWdV3oTjevrfyhX5n5qqg`
      );

      const dropOffdata = await dropOffResponse.json();
      const dropoffName =
        dropOffdata.results[0]?.formatted_address || "Location not found";
      return dropoffName;
    } catch (error) {
      console.error("Error fetching location name:", error);
      return null;
    }
  };

  useEffect(() => {
    if (!Array.isArray(listingLockDates)) {
      console.error("listingLockDates is not an array:", listingLockDates);
      return;
    }

    const formattedData = listingLockDates.map((date) => {
      // Format the date string with time information
      let timeInfo = null;
      
      if (date.time_ranges && date.time_ranges.length > 0) {
        // Display multiple time ranges
        timeInfo = (
          <div className="mt-1">
            <small className="text-muted">Time ranges:</small>
            {date.time_ranges.map((range, index) => (
              <div key={range.id} className="small">
                {range.start} - {range.end}
              </div>
            ))}
          </div>
        );
      } else if (date.start_time && date.end_time) {
        // Legacy single time range display
        timeInfo = (
          <div className="mt-1 small">
            {date.start_time} - {date.end_time}
          </div>
        );
      }

      const dateText = (
        <div>
          <p className="mb-1">
            {moment.utc(date.start_date).format("YYYY-MM-DD")}
            {" "}<span className="fw-cs">to</span>{" "}
            {moment.utc(date.end_date).format("YYYY-MM-DD")}
          </p>
          {timeInfo}
        </div>
      );

      return {
        date: dateText,
        button: (
          <Button
            className="btn btn-primary btn-cs-p"
            onClick={() => handleUnlockDate(date.id)}
          >
            Unlock
          </Button>
        ),
      };
    });

    setData(formattedData);
  }, [listingLockDates]);

  useEffect(() => {
    const fetchListingLocationName = async () => {
      try {
        if (initialData) {
          // Optimization: Use stored address text if available
          if (initialData.storageAddressText) {
            setStorageAddress(initialData.storageAddressText);
            // Ensure 'location' in formData is also set (if used)
            setFormData(prev => ({ ...prev, location: initialData.storageAddressText }));
            return;
          }

          // Fallback to Geocoding API only if text is missing
          if (initialData.storageAddress && initialData.storageAddress.lat && initialData.storageAddress.lng) {
            const locationName = await getLocationName(
              initialData.storageAddress.lat,
              initialData.storageAddress.lng
            );
            setStorageAddress(locationName);
            // We should ideally update the listing here with the text to "heal" it,
            // but for now we just display it. The user will save it when they edit.
          }
        }
      } catch (error) {
        console.log("Error ", error.message);
      }
    };
    fetchListingLocationName();
  }, [initialData]);

  useEffect(() => {
    console.log(initialData, "initialData?.boatStatus");
    if (initialData) {
      setSelectedStatus(initialData?.boatStatus);
      setSelectedValue(initialData?.boatCaptain);
      setPickupAvailable(initialData?.deliveryAndPickup || false);
      setFormData((prevData) => ({
        ...prevData,
        listingId: initialData.listingId || "",
        listingTitle: initialData.listingTitle || "",
        shortName: initialData.shortName || "",
        capacity: initialData.capacity || "",
        boatType: initialData.boatType || "",
        description: initialData.description || "",
        ownerId: initialData.ownerId || auth.currentUser.uid,
        featured_image: initialData.featured_image || null,
        storageAddress: initialData.storageAddress || "",
        make: initialData.make || "",
        model: initialData.model || "",
        year: initialData.year || "",
        boatLength: initialData.boatLength || "",
        engineType: initialData.engineType || "",
        hin: initialData.hin || "",
        images: initialData.images ? (Array.isArray(initialData.images) ? initialData.images : Object.values(initialData.images)) : [],
        motorMake: initialData.motorMake || "",
        motorModel: initialData.motorModel || "",
        motorYear: initialData.motorYear || "",
        motorSerial: initialData.motorSerial || "",
        trailerMake: initialData.trailerMake || "",
        trailerAxle: initialData.trailerAxle || "",
        trailerShocks: initialData.trailerShocks || "",
        trailerYear: initialData.trailerYear || "",
        trailerVin: initialData.trailerVin || "",
        trailerSpareWheel: initialData.trailerSpareWheel || "",
        trailerTireLegal: initialData.trailerTireLegal || "",
        trollingMotorMake: initialData.trollingMotorMake || "",
        trollingMotorModel: initialData.trollingMotorModel || "",
        trollingMotorYear: initialData.trollingMotorYear || "",
        trollingSpareTmProp: initialData.trollingSpareTmProp || "",
        trollingBigMotorProp: initialData.trollingBigMotorProp || "",
        shallowWaterAnchors: typeof initialData.shallowWaterAnchors === 'string'
          ? (initialData.shallowWaterAnchors.toLowerCase() === 'yes' ? '1' : initialData.shallowWaterAnchors.toLowerCase() === 'no' ? '2' : initialData.shallowWaterAnchors)
          : (initialData.shallowWaterAnchors || ""),
        shallowWaterAnchorBrandModel:
          initialData.shallowWaterAnchorBrandModel || "",
        ffsMake: initialData.ffsMake || "",
        ffsModel: initialData.ffsModel || "",
        ffsYear: initialData.ffsYear || "",
        graph1Make: initialData.graph1Make || "",
        graph1Model: initialData.graph1Model || "",
        graph1Year: initialData.graph1Year || "",
        graph2Make: initialData.graph2Make || "",
        graph2Model: initialData.graph2Model || "",
        graph2Year: initialData.graph2Year || "",
        graph3Make: initialData.graph3Make || "",
        graph3Model: initialData.graph3Model || "",
        graph3Year: initialData.graph3Year || "",
        graph4Make: initialData.graph4Make || "",
        graph4Model: initialData.graph4Model || "",
        graph4Year: initialData.graph4Year || "",
        insuranceProvider: initialData.insuranceProvider || "",
        insuranceName: initialData.insuranceName || "",
        insuranceNumber: initialData.insuranceNumber || "",
        insuranceCoverage: initialData.insuranceCoverage || "",
        liabilityCoverageAcknowledged: initialData.liabilityCoverageAcknowledged || false,
        numberOfBoats: initialData?.numberOfBoats || 1,
        fishingGuideDetails: {
          ...prevData.fishingGuideDetails,
          ...(initialData.fishingGuideDetails || {}),
        },
      }));

      // Initialize license document previews if they exist
      if (initialData.fishingGuideDetails?.legal?.licenses?.licenseDocuments) {
        const docs = initialData.fishingGuideDetails.legal.licenses.licenseDocuments;
        if (Array.isArray(docs)) {
          setLicenseDocumentPreviews(docs.map((doc, index) => ({
            name: `Document ${index + 1}`, // Default name as we might not have original filename
            url: doc
          })));
        }
      }

      if (initialData.pricing) {
        const {
          hourlyRate,
          hourlyOptions,
          dailyOptions,
          overnightEnabled,
          hours,
          maxPassengers,
          additionalPassengersAllowed,
          additionalPassengerPercentage,
          tripsStartingFrom,
        } = initialData.pricing;
        setPricing({
          hours: hours ?? 1,
          hourlyRate: hourlyRate ?? 0,
          maxPassengers: maxPassengers ?? 6,
          additionalPassengersAllowed: additionalPassengersAllowed ?? 10,
          additionalPassengerPercentage: additionalPassengerPercentage ?? 10,
          additionalPassengerPriceType: initialData.pricing?.additionalPassengerPriceType ?? "percentage",
          hourlyOptions: hourlyOptions ?? [],
          dailyOptions: dailyOptions ?? [],
          overnightEnabled: overnightEnabled ?? true,
          tripsStartingFrom: tripsStartingFrom ?? "",
        });
      }

      const initialSelectedCheckboxes = checkboxes.map((checkbox) => ({
        ...checkbox,
        checked: initialData.features?.includes(checkbox.label) || false,
      }));
      console.log(initialSelectedCheckboxes, "initialSelectedCheckboxes");
      setCheckboxes(initialSelectedCheckboxes);
      setSelectedCheckboxes(
        initialSelectedCheckboxes
          ?.filter((checkbox) => checkbox?.checked)
          ?.map((checkbox) => checkbox?.label)
      );
    }
    console.log("formData");
    console.log(formData);
  }, [initialData]);

  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);

  const [newCheckboxText, setNewCheckboxText] = useState("");

  // const handleCheckboxChange = (id, label) => {
  //   setCheckboxes((prevCheckboxes) =>
  //     prevCheckboxes.map((checkbox) =>
  //       checkbox.id === id
  //         ? { ...checkbox, checked: !checkbox.checked }
  //         : checkbox
  //     )
  //   );

  //   setSelectedCheckboxes((prevSelected) =>
  //     checkboxes
  //       .filter((checkbox) => checkbox.checked)
  //       .map((checkbox) => checkbox.label)
  //   );

  //   console.log('selectedCheckboxes');
  //   console.log(selectedCheckboxes);
  // };

  // useEffect(() => {
  console.log("selectedCheckboxes:", selectedCheckboxes);
  // }, [selectedCheckboxes]);

  const handleCheckboxChange = (id, label) => {
    setCheckboxes((prevCheckboxes) =>
      prevCheckboxes.map((checkbox) =>
        checkbox.id === id
          ? { ...checkbox, checked: !checkbox.checked }
          : checkbox
      )
    );

    setCheckboxes((updatedCheckboxes) => {
      setSelectedCheckboxes(
        updatedCheckboxes
          ?.filter((checkbox) => checkbox.checked)
          ?.map((checkbox) => checkbox.label)
      );
      return updatedCheckboxes;
    });
  };

  const handleAddCheckbox = (e) => {
    e.preventDefault(); // Prevent the form from submitting

    // Generate a unique id for the new checkbox
    const newCheckboxId = `checkbox_${new Date().getTime()}`;

    // Create a new checkbox object
    const newCheckbox = {
      id: newCheckboxId,
      label: newCheckboxText,
      checked: false,
    };

    // Update the state with the new checkbox
    setCheckboxes((prevCheckboxes) => [...prevCheckboxes, newCheckbox]);

    // Clear the input field
    setNewCheckboxText("");
  };

  const [success, setSuccess] = useState("");
  const handleStatusChange = async (event) => {
    try {
      const newStatus = event.target.value;
      setSelectedStatus(newStatus);
      await updateListingStatusById(initialData.listingId, newStatus);
      initialData.boatStatus = newStatus;
      setSuccess(`Listing status has been updated to ${newStatus}.`);
      setFormData((prevData) => ({ ...prevData, boatStatus: newStatus }));

      setTimeout(() => {
        setSuccess("");
      }, 1300);
    } catch (error) {
      setSuccess("");
    }
  };

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  async function handleLockDates() {
    try {
      if (initialData && initialData.listingId) {
        const listingsRef = ref(
          getDatabase(),
          `listings/${initialData.listingId}/lockDates`
        );

        // Keep the exact dates selected without any adjustments
        const startDateTimestamp = moment(lockStartDate).startOf("day");
        // .valueOf();
        const endDateTimestamp = moment(lockEndDate).startOf("day");

        // Prepare lock data with optional time information
        const lockData = {
          start_date: `${startDateTimestamp?._d}`,
          end_date: `${endDateTimestamp?._d}`,
        };

        // Add multiple time ranges if provided
        if (timeRanges.length > 0) {
          lockData.time_ranges = timeRanges;
        }

        await push(listingsRef, lockData);

        // When fetching the dates back
        const lockDates = await getListingLockDatesByID(initialData.id);
        const lockDatesArray = Object.entries(lockDates).map(([id, date]) => ({
          id,
          // Keep exact dates without adding days
          start_date: moment(date.start_date).format("YYYY-MM-DD"),
          end_date: moment(date.end_date).format("YYYY-MM-DD"),
          time_ranges: date.time_ranges || [],
          // Keep legacy single time fields for backward compatibility
          start_time: date.start_time || null,
          end_time: date.end_time || null,
        }));

        setListingLockDates(lockDatesArray);
        setSuccess("Dates have been locked.");
        setTimeRanges([]);
        setCurrentStartTime(null);
        setCurrentEndTime(null);

        setTimeout(() => {
          setSuccess("");
        }, 1300);
      }
    } catch (error) {
      console.error("Error In Lock Dates:", error);
      setSuccess("");
    }
  }

  // Function to save form data without completing the full submission process
  async function saveFormData() {
    try {
      setLoading(true);
      setIsFormDirty(false); // Form is saved, no longer dirty

      const featuresArray = selectedCheckboxes?.map((checkbox) => checkbox);

      // Only update if we have initialData with a listingId
      if (initialData && initialData.listingId) {
        const previousStatus = initialData.boatStatus;
        const newStatus = getBoatStatusAfterOwnerEdit(previousStatus);

        // Prepare listing details for update
        const listingDetails = {
          listingTitle: formData.listingTitle,
          shortName: formData.shortName,
          capacity: formData.capacity,
          boatType: formData.boatType,
          boatStatus: newStatus,
          description: formData.description,
          ownerId: initialData.ownerId || formData.ownerId,
          featured_image: formData.featured_image,
          featured_image: formData.featured_image,
          storageAddress: formData.storageAddress,
          storageAddressText: formData.storageAddressText,
          make: formData.make,
          model: formData.model,
          year: formData.year,
          boatLength: formData.boatLength,
          engineType: formData.engineType,
          hin: formData.hin,
          pricing,
          images: formData.images,
          motorMake: formData.motorMake,
          motorModel: formData.motorModel,
          motorYear: formData.motorYear,
          motorSerial: formData.motorSerial,
          trailerMake: formData.trailerMake,
          trailerAxle: formData.trailerAxle,
          trailerShocks: formData.trailerShocks,
          trailerYear: formData.trailerYear,
          trailerVin: formData.trailerVin,
          trailerSpareWheel: formData.trailerSpareWheel,
          trailerTireLegal: formData.trailerTireLegal,
          trollingMotorMake: formData.trollingMotorMake,
          trollingMotorYear: formData.trollingMotorYear,
          trollingMotorModel: formData.trollingMotorModel,
          trollingSpareTmProp: formData.trollingSpareTmProp,
          trollingBigMotorProp: formData.trollingBigMotorProp,
          shallowWaterAnchors: formData.shallowWaterAnchors,
          shallowWaterAnchorBrandModel: formData.shallowWaterAnchorBrandModel,
          ffsMake: formData.ffsMake,
          ffsModel: formData.ffsModel,
          ffsYear: formData.ffsYear,
          graph1Make: formData.graph1Make,
          graph1Model: formData.graph1Model,
          graph1Year: formData.graph1Year,
          graph2Make: formData.graph2Make,
          graph2Model: formData.graph2Model,
          graph2Year: formData.graph2Year,
          graph3Make: formData.graph3Make,
          graph3Model: formData.graph3Model,
          graph3Year: formData.graph3Year,
          graph4Make: formData.graph4Make,
          graph4Model: formData.graph4Model,
          graph4Year: formData.graph4Year,
          insuranceProvider: formData.insuranceProvider,
          insuranceName: formData.insuranceName,
          insuranceNumber: formData.insuranceNumber,
          insuranceCoverage: formData.insuranceCoverage,
          liabilityCoverageAcknowledged: formData.liabilityCoverageAcknowledged,
          features: featuresArray,
          priorityFeatures: priorityFeatures,
          boatCaptain: selectedValue ?? false,
          numberOfBoats: formData?.numberOfBoats,
          deliveryAndPickup: pickupAvailable,
          fishingGuideDetails: formData.fishingGuideDetails,
        };

        if (previousStatus === "Active") {
          listingDetails.boatListed = false;
        }
        if (newStatus === "Pending") {
          listingDetails.pendingAt = Date.now();
        }

        const listingsRef = ref(
          getDatabase(),
          `listings/${initialData.listingId}`
        );
        const filteredData = Object.fromEntries(
          Object.entries(listingDetails).filter(([_, v]) => v !== undefined)
        );
        
        const imagesToSave = filteredData.images;
        delete filteredData.images;

        await update(listingsRef, filteredData);

        if (imagesToSave !== undefined) {
          const imagesRef = ref(getDatabase(), `listings/${initialData.listingId}/images`);
          if (Array.isArray(imagesToSave) && imagesToSave.length === 0) {
            await remove(imagesRef); // Explicitly remove if empty to avoid Firebase array issues
          } else {
            await set(imagesRef, imagesToSave);
          }
        }

        initialData.boatStatus = newStatus;
        setSelectedStatus(newStatus);
        setFormData((prevData) => ({ ...prevData, boatStatus: newStatus }));

        notifyListingModified(
          formData.listingTitle,
          initialData.listingId
        ).catch((err) =>
          console.error("Failed to create admin notification:", err)
        );

        // Show success toast
        toast.success(
          previousStatus === "Active"
            ? "Boat updated and sent for admin approval."
            : "Boat details updated successfully!"
        );

        setSuccess("Changes saved successfully.");
        setTimeout(() => {
          setSuccess("");
        }, 2000);
      }
    } catch (e) {
      console.error("Error updating boat details:", e);
      // Show error toast
      toast.error(e.message || "Failed to update boat details. Please try again.");

      setError(e.message);
      setTimeout(() => {
        setError("");
      }, 2000);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      // Validate pricing fields
      const pricingErrors = validatePricingFields(pricing);
      if (Object.keys(pricingErrors).length > 0) {
        console.error('Pricing validation errors:', pricingErrors);
        toast.error('Please correct the pricing information before submitting.');
        setLoading(false);
        return;
      }
      setIsFormDirty(false); // Form is submitted, no longer dirty
      // Validate Bass Boat liability coverage acknowledgment
      if (formData.boatType?.toLowerCase() === "bass boat" && !formData.liabilityCoverageAcknowledged) {
        setError("You must acknowledge the liability coverage requirement for Bass Boats");
        setLoading(false);
        return;
      }

      setLoading(true);

      const featuresArray = selectedCheckboxes?.map((checkbox) => checkbox);

      // Store user information in the Realtime Database under 'listings' node
      const listingsRef = push(ref(getDatabase(), "listings/"));
      var boatStatus = "Pending";

      // if (formData.boatType == "Bass Boat") {
      //   boatStatus = "Pending";
      // } else {
      //   boatStatus = "Active";
      // }

      if (initialData) {
        if (initialData.boatStatus === "Active") {
          boatStatus = "Active";
        } else {
          boatStatus = "Pending";
        }
      }

      // Upload listing details
      const listingDetails = {
        listingTitle: formData.listingTitle,
        shortName: formData.shortName,
        capacity: formData.capacity,
        boatType: formData.boatType,
        boatStatus: boatStatus,
        description: formData.description,
        ownerId: initialData?.ownerId || formData.ownerId,
        featured_image: formData.featured_image,
        featured_image: formData.featured_image,
        storageAddress: formData.storageAddress,
        storageAddressText: formData.storageAddressText,
        make: formData.make,
        model: formData.model,
        year: formData.year,
        boatLength: formData.boatLength,
        engineType: formData.engineType,
        hin: formData.hin,
        pricing,
        images: formData.images,
        motorMake: formData.motorMake,
        motorModel: formData.motorModel,
        motorYear: formData.motorYear,
        motorSerial: formData.motorSerial,
        trailerMake: formData.trailerMake,
        trailerAxle: formData.trailerAxle,
        trailerShocks: formData.trailerShocks,
        trailerYear: formData.trailerYear,
        trailerVin: formData.trailerVin,
        trailerSpareWheel: formData.trailerSpareWheel,
        trailerTireLegal: formData.trailerTireLegal,
        trollingMotorMake: formData.trollingMotorMake,
        trollingMotorYear: formData.trollingMotorYear,
        trollingMotorModel: formData.trollingMotorModel,
        trollingSpareTmProp: formData.trollingSpareTmProp,
        trollingBigMotorProp: formData.trollingBigMotorProp,
        shallowWaterAnchors: formData.shallowWaterAnchors,
        shallowWaterAnchorBrandModel: formData.shallowWaterAnchorBrandModel,
        ffsMake: formData.ffsMake,
        ffsModel: formData.ffsModel,
        ffsYear: formData.ffsYear,
        graph1Make: formData.graph1Make,
        graph1Model: formData.graph1Model,
        graph1Year: formData.graph1Year,
        graph2Make: formData.graph2Make,
        graph2Model: formData.graph2Model,
        graph2Year: formData.graph2Year,
        graph3Make: formData.graph3Make,
        graph3Model: formData.graph3Model,
        graph3Year: formData.graph3Year,
        graph4Make: formData.graph4Make,
        graph4Model: formData.graph4Model,
        graph4Year: formData.graph4Year,
        insuranceProvider: formData.insuranceProvider,
        insuranceName: formData.insuranceName,
        insuranceNumber: formData.insuranceNumber,
        insuranceCoverage: formData.insuranceCoverage,
        liabilityCoverageAcknowledged: formData.liabilityCoverageAcknowledged,
        features: featuresArray,
        priorityFeatures: priorityFeatures,
        boatCaptain: selectedValue ?? false,
        numberOfBoats: formData?.numberOfBoats,
        deliveryAndPickup: pickupAvailable,
        fishingGuideDetails: formData.fishingGuideDetails,
      };

      // Add Images property and set it to an array of image URLs if selectedImages is an array
      // if (Array.isArray(selectedImages) && selectedImages.length > 0) {
      //   listingDetails.images = selectedImages.map((image) => URL.createObjectURL(image));
      // }

      // Update the featured_image property if it has been set
      if (formData.featured_image) {
        listingDetails.featured_image = formData.featured_image;
      }

      if (initialData) {
        if (initialData.listingId) {
          const previousStatus = initialData.boatStatus;
          const newStatus = getBoatStatusAfterOwnerEdit(previousStatus);
          listingDetails.ownerId = initialData.ownerId;
          listingDetails.boatStatus = newStatus;
          if (previousStatus === "Active") {
            listingDetails.boatListed = false;
          }
          if (newStatus === "Pending") {
            listingDetails.pendingAt = Date.now();
          }
          listingDetails.features = selectedCheckboxes;
          const listingsRef = ref(
            getDatabase(),
            `listings/${initialData.listingId}`
          );
          const filteredData = Object.fromEntries(
            Object.entries(listingDetails).filter(([_, v]) => v !== undefined)
          );
          
          const imagesToSave = filteredData.images;
          delete filteredData.images;

          await update(listingsRef, filteredData);

          if (imagesToSave !== undefined) {
            const imagesRef = ref(getDatabase(), `listings/${initialData.listingId}/images`);
            if (Array.isArray(imagesToSave) && imagesToSave.length === 0) {
              await remove(imagesRef);
            } else {
              await set(imagesRef, imagesToSave);
            }
          }

          notifyListingModified(
            formData.listingTitle,
            initialData.listingId
          ).catch((err) =>
            console.error("Failed to create admin notification:", err)
          );

          initialData.boatStatus = newStatus;
          setSelectedStatus(newStatus);
          setFormData((prevData) => ({ ...prevData, boatStatus: newStatus }));
        }
      } else {
        const now = Date.now();
        const filteredDetails = Object.fromEntries(
          Object.entries({
            ...listingDetails,
            pendingAt: now,
            createdAt: now,
          }).filter(([_, v]) => v !== undefined)
        );
        await set(listingsRef, filteredDetails);

        const ownerDetails = await getUserByID(formData.ownerId);
        const ownerName = ownerDetails.firstName + " " + ownerDetails.lastName;
        const bbrId = ownerDetails.bbrId;
        const adminEmailBody = await newListingAddedAdminEmailBody(
          ownerName,
          bbrId,
          formData.listingTitle,
          formData.boatType === "Other" ? "" : formData.boatType
        );
        await sendEmail(
          "waivers@bbrboatrentals.com",
          "New Bass Boat Listing Added.",
          adminEmailBody
        );

        notifyNewBoat(formData.listingTitle, listingsRef.key).catch((err) =>
          console.error("Failed to create admin notification:", err)
        );

        if (targetOwnerId) {
          notifyOwnerBoatAddedByAdmin(
            targetOwnerId,
            formData.listingTitle,
            listingsRef.key
          ).catch((err) =>
            console.error("Failed to notify boat owner:", err)
          );
        }
      }

      if (targetOwnerId) {
        navigate(`/dashboard/user/${targetOwnerId}`);
      } else {
        navigate("/dashboard/my-listings");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  const handleDeleteListing = async () => {
    try {
      const database = getDatabase();
      const listingRef = ref(database, `listings/${initialData.listingId}`);
      await remove(listingRef);
      setError("Listing has been deleted.");

      // Adding a timeout of 1.3 seconds before navigating
      setTimeout(() => {
        navigate("/dashboard/my-listings");
      }, 1300);
    } catch (error) {
      console.log(
        `Listing ${initialData.listingId} could not be removed, Error `,
        error
      );
    }
  };

  const [inputTitle, setInputTitle] = useState("");
  const [inputShortTitle, setInputShortTitle] = useState("");
  const [inputDescription, setInputDescription] = useState("");
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
    setIsFormDirty(true);
  };

  const generateAIDescription = async () => {
    try {
      setIsGeneratingDescription(true);

      // Gather relevant data for the AI
      const descriptionData = {
        boatType: formData.boatType,
        make: formData.make,
        model: formData.model,
        year: formData.year,
        capacity: formData.capacity,
        features: selectedCheckboxes
      };

      const response = await fetch(`${serverUrl}/api/generate-description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(descriptionData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate description');
      }

      const data = await response.json();

      // Update the description field
      setFormData(prevData => ({
        ...prevData,
        description: data.description
      }));

      toast.success('AI-generated description added!');
    } catch (error) {
      console.error('Error generating description:', error);
      toast.error('Failed to generate description. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleNestedChange = (path, value) => {
    setFormData((prevData) => {
      const keys = path.split('.');
      const newData = { ...prevData };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleLicenseDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    const formDataUpload = new FormData();

    files.forEach((file) => {
      formDataUpload.append("files", file);
    });

    try {
      const response = await fetch(`${serverUrl}/upload-images`, {
        method: "POST",
        body: formDataUpload,
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log("Document Upload Response:", responseData);

        if (responseData.success) {
          const uploadedUrls = responseData.file_names.map(
            (filename) => `${serverUrl}/images/${filename}`
          );

          // Update previews
          setLicenseDocumentPreviews((prevPreviews) => [
            ...prevPreviews,
            ...files.map((file, index) => ({
              name: file.name,
              type: file.type,
              url: uploadedUrls[index], // Store the URL
            })),
          ]);

          // Update formData with URLs
          setFormData((prevData) => {
            const newData = { ...prevData };
            const details = { ...newData.fishingGuideDetails };
            const legal = { ...details.legal };
            const licenses = { ...legal.licenses };

            const currentDocs = licenses.licenseDocuments || [];
            licenses.licenseDocuments = [...currentDocs, ...uploadedUrls];

            legal.licenses = licenses;
            details.legal = legal;
            newData.fishingGuideDetails = details;
            return newData;
          });

        } else {
          console.error("Document upload failed:", responseData.message);
          toast.error("Failed to upload document.");
        }
      } else {
        console.error("Server error during document upload");
        toast.error("Failed to upload document.");
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error("Error uploading documents.");
    }
  };

  const removeLicenseDocument = (index) => {
    setLicenseDocumentPreviews((prevPreviews) => {
      const newPreviews = [...prevPreviews];
      newPreviews.splice(index, 1);
      return newPreviews;
    });

    setFormData((prevData) => {
      const newData = { ...prevData };
      const details = { ...newData.fishingGuideDetails };
      const legal = { ...details.legal };
      const licenses = { ...legal.licenses };

      const currentDocs = [...(licenses.licenseDocuments || [])];
      currentDocs.splice(index, 1);
      licenses.licenseDocuments = currentDocs;

      legal.licenses = licenses;
      details.legal = legal;
      newData.fishingGuideDetails = details;
      return newData;
    });
  };

  useEffect(() => {
    setInputTitle(formData.listingTitle);
    setInputShortTitle(formData.shortName);
    setInputDescription(formData.description);
  }, [formData]);

  // Set form type when component mounts
  useEffect(() => {
    setFormType('boat');
    return () => {
      // Clean up when component unmounts
      setIsFormDirty(false);
      setFormType('');
    };
  }, [setFormType, setIsFormDirty]);

  // Mark form as dirty when any field changes
  const handleFormChange = useCallback(() => {
    setIsFormDirty(true);
  }, [setIsFormDirty]);

  // Prevent number inputs from changing on mouse wheel scroll
  useEffect(() => {
    const handleWheel = (e) => {
      if (document.activeElement && document.activeElement.type === "number") {
        document.activeElement.blur();
      }
    };
    
    document.addEventListener("wheel", handleWheel, { capture: true, passive: true });
    
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, []);

  // Add event listeners to form fields
  useEffect(() => {
    const formElements = document.querySelectorAll('input, textarea, select');
    formElements.forEach(element => {
      element.addEventListener('change', handleFormChange);
      element.addEventListener('input', handleFormChange);
    });

    return () => {
      formElements.forEach(element => {
        element.removeEventListener('change', handleFormChange);
        element.removeEventListener('input', handleFormChange);
      });
    };
  }, [handleFormChange]);

  const nextStep = () => {
    // Skip step 3 when coming from step 2 for Fishing Charter boat type
    if (step === 2 && formData.boatType === "Fishing Charter") {
      setStep((prevStep) => prevStep + 2); // Skip to step 4
    } else {
      setStep((prevStep) => prevStep + 1);
    }
  };

  const prevStep = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const validateStep = (stepNumber) => {
    if (stepNumber === 1) {
      if (!formData?.listingTitle?.trim()?.length) {
        toast.error("Listing Title is required.");
        return false;
      }
      if (!Boolean(formData.capacity)) {
        toast.error("Passengers Capacity is required.");
        return false;
      }
      if (!Boolean(formData.boatType)) {
        toast.error("Boat Type is required.");
        return false;
      }
      if (formData.boatType?.toLowerCase() === "bass boat" && !formData.liabilityCoverageAcknowledged) {
        toast.error("You must acknowledge the liability coverage requirement for Bass Boats.");
        return false;
      }
    }
    if (stepNumber === 2 && formData.boatType !== "Fishing Charter") {
      if (!storageAddress || storageAddress.trim().length === 0) {
        toast.error("Boat Location is required.");
        return false;
      }
    }
    if (stepNumber === 4 && formData.boatType === "Fishing Charter") {
      if (!storageAddress || storageAddress.trim().length === 0) {
        toast.error("Departure Location is required.");
        return false;
      }
    }
    return true;
  };

  const handleStepBarClick = (targetStep) => {
    if (targetStep === step) return;
    // Going backward is always allowed
    if (targetStep < step) {
      setStep(targetStep);
      return;
    }
    // Going forward: validate all steps in between
    const stepsConfig = formData.boatType === "Fishing Charter"
      ? [1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]
      : [1, 2, 3, 4, 5];
    const currentIdx = stepsConfig.indexOf(step);
    const targetIdx = stepsConfig.indexOf(targetStep);
    for (let i = currentIdx; i < targetIdx; i++) {
      if (!validateStep(stepsConfig[i])) {
        setStep(stepsConfig[i]);
        return;
      }
    }
    setStep(targetStep);
  };

  const [charterOption, setCharterOption] = useState();
  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleRadioChange = (questionText, option) => {
    // Check if an option for the same question already exists
    const existingOptionIndex = selectedOptions.findIndex(
      (selectedOption) => selectedOption.questionText === questionText
    );

    if (existingOptionIndex !== -1) {
      // If an option exists, update the existing option
      setSelectedOptions((prevOptions) => {
        const updatedOptions = [...prevOptions];
        updatedOptions[existingOptionIndex] = {
          questionText,
          option,
        };
        return updatedOptions;
      });
    } else {
      // If no option exists, add a new option to the array
      setSelectedOptions((prevOptions) => [
        ...prevOptions,
        {
          questionText,
          option,
        },
      ]);
    }
    setCharterOption(option);
  };
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [licenseDocuments, setLicenseDocuments] = useState([]);
  const [licenseDocumentPreviews, setLicenseDocumentPreviews] = useState([]);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);

    setSelectedImages(files);

    const formDataUpload = new FormData();

    files.forEach((file) => {
      formDataUpload.append("files", file); // Use 'files' as the key for each file
    });

    // Generate previews for images
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
    try {
      const response = await fetch(`${serverUrl}/upload-images`, {
        method: "POST",
        body: formDataUpload,
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log("Server Response:", responseData);

        if (responseData.success) {
          // Use the server's response to update the state with image URLs
          const newImageUrls = responseData.file_names.map(
            (filename) => `${serverUrl}/images/${filename}`
          );

          setFormData((prevDetails) => {
            const currentImages = Array.isArray(prevDetails.images) ? prevDetails.images : [];
            return {
              ...prevDetails,
              images: [...currentImages, ...newImageUrls],
            };
          });
          setImagePreviews([]);

          console.log("formData Images updated");
        } else {
          console.error(
            "File upload failed. Server returned:",
            responseData.message
          );
        }
      } else {
        console.error("File upload failed. Server returned:", response);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      console.error("Error stack:", error.stack);
    }
  };

  const removeGalleryImage = (indexToRemove) => {
    setFormData((prevDetails) => {
      const currentImages = Array.isArray(prevDetails.images) ? prevDetails.images : [];
      return {
        ...prevDetails,
        images: currentImages.filter((_, index) => index !== indexToRemove)
      };
    });
  };

  const [selectedFeaturedImage, setSelectedFeaturedImage] = useState(null);

  const handleFeaturedImageChange = async (e) => {
    const file = e.target.files[0];

    console.log(file);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${serverUrl}/upload-featured-image`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const responseData = await response.json();
        setFormData((prevDetails) => ({
          ...prevDetails,
          featured_image: `${serverUrl}/images/${responseData.file_name}`, // Adjust to the actual path relative to your application directory
        }));
        setSelectedFeaturedImage(
          `${serverUrl}/images/${responseData.file_name}`
        );
      } else {
        console.error("File upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const [address, setAddress] = useState("");
  const [storageAddress, setStorageAddress] = useState("");

  const handlesStorageSelect = async (selectedAddress) => {
    try {
      const results = await geocodeByAddress(selectedAddress);
      const latLng = await getLatLng(results[0]);
      setFormData((prevData) => ({
        ...prevData,
        storageAddress: latLng,
        storageAddressText: results[0].formatted_address
      }));
      setStorageAddress(results[0].formatted_address);
    } catch (error) {
      console.error("Error fetching geolocation:", error);
    }
  };

  const handleUnlockDate = async (id) => {
    try {
      const listingsRef = ref(
        getDatabase(),
        `listings/${initialData.listingId}/lockDates/${id}`
      );
      await remove(listingsRef, "test test");
      console.log("Lock date deleted successfully.");

      // Remove the item from listingLockDates array
      const updatedListingLockDates = listingLockDates.filter(
        (date) => date.id !== id
      );
      setListingLockDates(updatedListingLockDates);
      setSuccess("Date has been unlocked.");
      setTimeout(() => {
        setSuccess("");
      }, 1300);
    } catch (error) {
      console.error("Error deleting lock date:", error);
      // You can add additional error handling here
    }
  };

  const [data, setData] = useState({});

  const columns = [
    {
      Header: "Date",
      accessor: "date",
    },
    {
      Header: "Button",
      accessor: "button",
    },
    // Add more columns as needed
  ];


  const handleDriveRadioChange = (e) => {
    setSelectedValue(e.target.value === true);
  };
  // Function to fetch user business information
  const fetchUserBusinessInfo = async () => {
    try {
      const ownerUserId = targetOwnerId || auth.currentUser?.uid;
      const userData = targetOwnerId
        ? await getUserByID(targetOwnerId)
        : await getCurrentUserDetails();
      if (userData) {
        console.log('userData...', userData);

        // Get owner metadata which might contain business information
        let businessName = '';
        let businessType = '';
        let country = '';
        let state = '';
        let city = '';
        let website = '';

        try {
          // Import the function if not already imported
          const { getOwnerMetaByID } = await import('./utils');
          const ownerMetaData = await getOwnerMetaByID(ownerUserId);
          console.log('ownerMetaData...', ownerMetaData);

          if (ownerMetaData) {
            // Extract business information from ownerMeta if available
            businessName = ownerMetaData.businessName || ownerMetaData.businessInfo?.name || '';
            businessType = ownerMetaData.businessType || ownerMetaData.businessInfo?.type || '';
            country = ownerMetaData.country || ownerMetaData.businessInfo?.country || '';
            state = ownerMetaData.state || ownerMetaData.businessInfo?.state || '';
            city = ownerMetaData.city || ownerMetaData.businessInfo?.city || '';
            website = ownerMetaData.website || ownerMetaData.businessInfo?.website || '';
          }
        } catch (metaError) {
          console.error("Error fetching owner metadata:", metaError);
        }

        // Create default business name based on user's name if not available
        if (!businessName && userData.firstName) {
          businessName = `${userData.firstName}'s Fishing Charter`;
        }

        // Default business type if not available
        if (!businessType) {
          businessType = 'Sole Proprietor';
        }

        // Pre-fill fishing guide details with user data, owner metadata, and defaults
        setFormData(prevData => ({
          ...prevData,
          fishingGuideDetails: {
            ...prevData.fishingGuideDetails,
            primaryContact: {
              fullName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
              role: userData.role || '',
              email: userData.email || '',
              mobile: userData.phone || '',
              alternatePhone: userData.alternatePhone || '',
            },
            businessInfo: {
              ...prevData.fishingGuideDetails.businessInfo,
              name: businessName,
              type: businessType,
              country: country || userData.country || '',
              state: state || userData.state || '',
              city: city || userData.city || '',
              website: website || userData.website || '',
            }
          }
        }));
      }
    } catch (error) {
      console.error("Error fetching user business information:", error);
    }
  };

  useEffect(() => {
    if ((formData.boatType === "Fishing Charter" || formData.boatType === "Fishing Guide") && !initialData) {
      setSelectedValue(true); // Set captain to true for fishing guides/charters
      fetchUserBusinessInfo(); // Pre-fill business information from user account
    }
  }, [formData.boatType]);

  // Initial load of user business info when component mounts
  useEffect(() => {
    if (auth.currentUser && !initialData) {
      fetchUserBusinessInfo();
    }
  }, []);

  useEffect(() => {
    if (!targetOwnerId) return;

    const setTargetOwner = async () => {
      try {
        const currentUser = await getCurrentUserDetails();
        if (currentUser?.role !== "Admin") {
          navigate("/dashboard/add-listing");
          return;
        }

        setFormData((prevData) => ({
          ...prevData,
          ownerId: targetOwnerId,
        }));

        const ownerData = await getUserByID(targetOwnerId);
        if (ownerData) {
          setOwnerName(`${ownerData.firstName} ${ownerData.lastName}`);
        }
      } catch (error) {
        console.error("Error setting target owner:", error);
        toast.error("Could not load boat owner details.");
      }
    };

    setTargetOwner();
  }, [targetOwnerId, navigate]);

  return (
    <>
      {targetOwnerId && ownerName !== "-" && (
        <div className="mb-3">
          <Alert severity="info">
            Adding a boat on behalf of <strong>{ownerName}</strong>
          </Alert>
        </div>
      )}
      <Row className="px-2">
        <Col className="p-4 card">
          <Row className="mobile-direction-column-only">
            <Col xs={12} md={3}>
              <img
                className="thumbnailImg rounded"
                src={
                  selectedFeaturedImage
                    ? selectedFeaturedImage
                    : initialData && initialData.featured_image
                      ? initialData.featured_image
                      : "/assets/backend/images/featured-img.webp"
                }
                alt="Featured Image"
              />
            </Col>
            <Col xs={12} md={6}>
              <h1 className="margin-top-mobile-10">
                {inputTitle || "Title here"}
              </h1>
              <h5>{inputShortTitle || "Short title here"}</h5>
              <p>{inputDescription || "Description here"}</p>
              <h5>Owner Name</h5>
              <p>{ownerName || "-"}</p>
            </Col>
            <Col xs={12} md={3} className="d-flex flex-column">
              {initialData && userDetails.role === "Admin" ? (
                <div class="d-flex justify-content-center mb-2 mt-4">
                  <select
                    className="form-select"
                    name="status"
                    aria-label="Default select example"
                    value={selectedStatus}
                    onChange={(e) => handleStatusChange(e)}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Active">Active</option>
                    <option value="Reject">Deny</option>
                  </select>
                </div>
              ) : (
                ""
              )}

              {initialData && (
                <button
                  type="button"
                  onClick={openLockDatesModal}
                  className="btn btn-primary mt-2"
                >
                  <i className="material-icons">https</i>Manage Availability
                </button>
              )}
              {initialData && (
                <button
                  type="button"
                  onClick={handleDeleteListing}
                  className="btn btn-danger mt-2"
                >
                  <i className="material-icons">delete_outline</i>Remove
                </button>
              )}
              {success && (
                <Alert className="mt-3" severity="success">
                  {success}
                </Alert>
              )}
              {error && (
                <Alert className="mt-3" severity="error">
                  {error}
                </Alert>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="row">
        <div className="col">
          <div className="card calendar-container">
            <div className="card-body">
              <div className="auth-credentials m-b-xxl">
                <Row>
                  <Col>
                    {(() => {
                      const fishingCharterSteps = [
                        { step: 1, label: "Basic Info" },
                        { step: 2, label: "Owner Details" },
                        { step: 4, label: "Location" },
                        { step: 5, label: "Vessel" },
                        { step: 6, label: "Fishing Exp." },
                        { step: 7, label: "Inclusions" },
                        { step: 8, label: "Pricing" },
                        { step: 9, label: "Calendar" },
                        { step: 10, label: "Policies" },
                        { step: 11, label: "Legal" },
                        { step: 12, label: "Photos" },
                        { step: 13, label: "House Rules" },
                      ];
                      const regularSteps = [
                        { step: 1, label: "Basic Info" },
                        { step: 2, label: "Location" },
                        { step: 3, label: "Gallery" },
                        { step: 4, label: "Specs & Pricing" },
                        { step: 5, label: "Cancellation Policy" },
                      ];
                      const steps = formData.boatType === "Fishing Charter" ? fishingCharterSteps : regularSteps;
                      const currentIndex = steps.findIndex(s => s.step === step);
                      return (
                        <div className="step-bar-container mb-4">
                          <div className="step-bar">
                            {steps.map((s, index) => (
                              <div
                                key={s.step}
                                className={`step-bar-item ${s.step === step ? "active" : ""} ${steps.findIndex(x => x.step === step) > index ? "completed" : ""}`}
                                onClick={() => handleStepBarClick(s.step)}
                                title={s.label}
                              >
                                <div className="step-bar-circle">
                                  {steps.findIndex(x => x.step === step) > index ? (
                                    <i className="fas fa-check" style={{ fontSize: "10px" }}></i>
                                  ) : (
                                    index + 1
                                  )}
                                </div>
                                <span className="step-bar-label">{s.label}</span>
                                {index < steps.length - 1 && <div className={`step-bar-line ${steps.findIndex(x => x.step === step) > index ? "completed" : ""}`}></div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <Form onSubmit={handleSubmit}>
                      {step === 1 && (
                        <>
                          <Form.Group controlId="listingTitle">
                            <Row className="mb-2">
                              <Col xs={12} md={6}>
                                <Form.Label className="form-label">
                                  Listing Title
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  name="listingTitle"
                                  value={formData.listingTitle}
                                  onChange={handleChange}
                                  className="form-control"
                                  required
                                />
                              </Col>
                              <Col xs={12} md={6}>
                                <Form.Label>Short Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="shortName"
                                  value={formData.shortName}
                                  onChange={handleChange}
                                  className="form-control"
                                  required
                                />
                              </Col>
                            </Row>

                            <Row className="mb-2">

                              <Col xs={12} md={6}>
                                <Form.Label>Boat Type</Form.Label>
                                <Form.Control
                                  as="select" // Set the input type to "select"
                                  name="boatType"
                                  value={formData.boatType}
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      numberOfBoats: 1,
                                    }));
                                    handleChange(e);
                                  }}
                                  className="form-control"
                                  required
                                >
                                  <option value="" selected>
                                    Select your boat type
                                  </option>
                                  {boatTypes.map((type, index) => (
                                    <option key={index} value={type.value}>
                                      {type.value}
                                    </option>
                                  ))}
                                </Form.Control>
                              </Col>

                              <Col xs={12} md={6}>
                                <Form.Label>Max Passengers Capacity*</Form.Label>
                                <Form.Control
                                  type="number"
                                  name="capacity"
                                  value={formData.capacity}
                                  className="form-control"
                                  onChange={handleChange}
                                  required
                                />
                              </Col>

                            </Row>

                            <Row className="mb-2">
                              <Col xs={12}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <Form.Label className="mb-0">Description</Form.Label>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={generateAIDescription}
                                    disabled={isGeneratingDescription || !formData.boatType}
                                    style={{ backgroundColor: '#fff' }}
                                  >
                                    {isGeneratingDescription ? (
                                      <>
                                        <Spinner
                                          as="span"
                                          animation="border"
                                          size="sm"
                                          role="status"
                                          aria-hidden="true"
                                        />
                                        <span className="ms-1">Writing...</span>
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-magic me-1"></i> AI Write Description
                                      </>
                                    )}
                                  </Button>
                                </div>
                                <Form.Control
                                  as="textarea"
                                  rows={5}
                                  name="description"
                                  value={formData.description}
                                  className="form-control"
                                  onChange={handleChange}
                                  required
                                />
                                {!formData.boatType && (
                                  <small className="text-muted mt-1 d-block">
                                    Select a boat type first to enable AI description generation
                                  </small>
                                )}
                              </Col>
                            </Row>
                          </Form.Group>
                          <Row className="mb-2">

                            {(formData?.boatType?.toLowerCase() === "kayak" ||
                              formData?.boatType?.toLowerCase() ===
                              "jetski") && (
                                <Col xs={12} className="my-1">
                                  <Form.Label>
                                    Available number of {formData?.boatType} boats
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="numberOfBoats"
                                    min={1}
                                    value={formData.numberOfBoats}
                                    className="form-control"
                                    onChange={handleChange}
                                    required
                                  />
                                </Col>
                              )}

                            {formData?.boatType?.toLowerCase() === "bass boat" && (
                              <>
                                <Col xs={12} className="mb-3">
                                  <Form.Label>Liability Coverage</Form.Label>
                                  <div className="p-3" style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "0.375rem" }}>
                                    <p className="mb-3" style={{ fontSize: "14px", lineHeight: "1.5", color: "#6c757d" }}>
                                      To list a Bass boat on BBR Boat rentals platform, you will need to have an insurance policy which has a minimum of $300,000 USD liability coverage. You need to be able to provide a copy of this policy to your renters for each trip. The boat owner is responsible for providing the necessary coverage, BBR recommends a peer-to-peer rental addendum to be added to your policy. Please check the box to acknowledge this statement.
                                    </p>
                                    <Form.Check
                                      type="checkbox"
                                      label="I acknowledge the liability coverage requirement"
                                      name="liabilityCoverageAcknowledged"
                                      checked={formData.liabilityCoverageAcknowledged || false}
                                      onChange={handleChange}
                                      required
                                      style={{ fontSize: "14px" }}
                                    />
                                  </div>
                                </Col>
                              </>
                            )}

                            <Col xs={12} style={{ margin: "1rem 0" }}>
                              <Form.Group>
                                <Form.Label>
                                  Who can drive this boat?
                                </Form.Label>
                                <Form.Check
                                  type="radio"
                                  label="Only Boat Owner can drive this boat."
                                  value="onlyOwner"
                                  name="options"
                                  checked={selectedValue}
                                  onChange={(e) =>
                                    handleDriveRadioChange({
                                      target: { value: true },
                                    })
                                  }
                                  disabled={
                                    formData.boatType === "Fishing Charter"
                                  }
                                />
                                <Form.Check
                                  type="radio"
                                  label="Both Renters & Boat Owner are allowed to drive this boat."
                                  value="bothRenterAndOwner"
                                  name="options"
                                  checked={!selectedValue}
                                  onChange={(e) =>
                                    handleDriveRadioChange({
                                      target: { value: false },
                                    })
                                  }
                                  disabled={
                                    formData.boatType === "Fishing Charter"
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col xs={12} style={{ margin: "1rem 0" }}>
                              <Form.Group>
                                <Form.Label>
                                  Delivery and Pickup Services
                                </Form.Label>
                                <Form.Check
                                  type="radio"
                                  label="Available."
                                  value="available"
                                  checked={pickupAvailable}
                                  onChange={(e) =>
                                    setPickupAvailable(e.target.checked)
                                  }
                                  disabled={
                                    formData.boatType === "Fishing Charter"
                                  }
                                />
                                <Form.Check
                                  type="radio"
                                  label="Not Available."
                                  value="notAvailable"
                                  checked={!pickupAvailable}
                                  onChange={(e) =>
                                    setPickupAvailable(
                                      e.target.checked ? false : true
                                    )
                                  }
                                  disabled={
                                    formData.boatType === "Fishing Charter"
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3 mt-3">
                            <Col xs={12} className="mb-3">
                              <Form.Group>
                                <Form.Label>Featured Image</Form.Label>
                                <Form.Control
                                  type="file"
                                  name="featuredImage"
                                  onChange={handleFeaturedImageChange}
                                  accept="image/*"
                                />
                                {formData.featured_image && (
                                  <div className="mt-2">
                                    <img
                                      src={formData.featured_image}
                                      alt="Featured Preview"
                                      style={{ width: "200px", height: "auto", borderRadius: "8px" }}
                                    />
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="me-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="btn btn-primary"
                                onClick={() => {
                                  if (
                                    formData?.listingTitle?.trim()?.length < 1
                                  ) {
                                    toast.error("Listing Title is required.");
                                  } else if (!Boolean(formData.capacity)) {
                                    toast.error(
                                      "Passengers Capacity is required."
                                    );
                                  } else if (!Boolean(formData.boatType)) {
                                    toast.error("Boat Type is required.");
                                  } else if (formData.boatType?.toLowerCase() === "bass boat" && !formData.liabilityCoverageAcknowledged) {
                                    toast.error("You must acknowledge the liability coverage requirement for Bass Boats.");
                                  } else {
                                    nextStep();
                                  }
                                }}
                              >
                                Next
                              </Button>
                            </Col>
                          </Row>
                        </>
                      )}
                      {step === 2 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>1. Account / Business Owner Details</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12}>
                              <h5>1.1 Account Holder / Primary Contact</h5>
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Full Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.primaryContact.fullName}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.primaryContact.fullName", e.target.value)}
                                className="form-control"
                                required
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Role (Owner / Captain / Manager / Other)</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.primaryContact.role}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.primaryContact.role", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Email Address</Form.Label>
                              <Form.Control
                                type="email"
                                value={formData.fishingGuideDetails.primaryContact.email}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.primaryContact.email", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Mobile Phone</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.primaryContact.mobile}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.primaryContact.mobile", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Alternate Phone (optional)</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.primaryContact.alternatePhone}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.primaryContact.alternatePhone", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                          </Row>

                          <Row className="mb-3 mt-4">
                            <Col xs={12}>
                              <h5>1.2 Business Information</h5>
                            </Col>
                            <Col xs={12} md={12} className="mb-2">
                              <Form.Label>Business / Charter Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.businessInfo.name}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.name", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Business Type</Form.Label>
                              <Form.Control
                                as="select"
                                value={formData.fishingGuideDetails.businessInfo.type}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.type", e.target.value)}
                                className="form-control"
                              >
                                <option value="">Select Type</option>
                                <option value="Sole Proprietor">Sole Proprietor</option>
                                <option value="LLC">LLC</option>
                                <option value="Corporation">Corporation</option>
                                <option value="Other">Other</option>
                              </Form.Control>
                              {formData.fishingGuideDetails.businessInfo.type === "Other" && (
                                <Form.Control
                                  type="text"
                                  placeholder="Specify Other"
                                  className="form-control mt-2"
                                  onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.typeOther", e.target.value)}
                                />
                              )}
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Country</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.businessInfo.country}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.country", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>State / Region / Province</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.businessInfo.state}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.state", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>City</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.businessInfo.city}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.city", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} className="mb-2">
                              <Form.Label>Website (if applicable)</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.businessInfo.website}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.website", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}
                      {step === 2 && formData.boatType !== "Fishing Charter" && (
                        <>
                          <Row className="mb-2">
                            <Col xs={12}>
                              <Form.Label>Boat Location*</Form.Label>
                              <div className="locationDiv">
                                <PlacesAutocomplete
                                  value={storageAddress}
                                  onChange={setStorageAddress}
                                  onSelect={handlesStorageSelect}
                                >
                                  {({
                                    getInputProps,
                                    suggestions,
                                    getSuggestionItemProps,
                                    loading,
                                  }) => (
                                    <div>
                                      <div className="input-group">
                                        <input
                                          {...getInputProps({
                                            placeholder: "Search Places...",
                                            className:
                                              "location-search-input form-control",
                                            name: "location",
                                          })}
                                        />
                                        {/* <div className="input-group-append d-flex">
                                              <button
                                                className="btn btn-outline-dark"
                                                type="button"
                                                onClick={handleCurrentLocation}
                                              >
                                                <i className="fas fa-map-marker-alt"></i>
                                              </button>
                                            </div> */}
                                      </div>
                                      <div className="autocomplete-dropdown-container">
                                        {loading && <div>Loading...</div>}
                                        {suggestions.map((suggestion) => {
                                          const className = suggestion.active
                                            ? "suggestion-item--active"
                                            : "suggestion-item";
                                          return (
                                            <div
                                              {...getSuggestionItemProps(
                                                suggestion,
                                                {
                                                  className:
                                                    "location-suggestion form-control",
                                                }
                                              )}
                                            >
                                              <span>
                                                {suggestion.description}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </PlacesAutocomplete>
                              </div>
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button
                                className=""
                                variant="secondary"
                                onClick={prevStep}
                              >
                                Previous
                              </Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button
                                className="mx-2"
                                variant="primary"
                                onClick={() =>
                                  storageAddress.trim().length > 0
                                    ? nextStep()
                                    : toast.error("Boat Location is required.")
                                }
                              >
                                Next
                              </Button>
                            </Col>
                          </Row>
                        </>
                      )}
                      {step === 3 && formData.boatType === "Fishing Charter" && (
                        <>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}
                      {step === 3 && formData.boatType !== "Fishing Charter" && (
                        <>
                          <Form.Group>
                            <Form.Label>Add Gallery Images</Form.Label>
                            <Form.Control
                              type="file"
                              name="images"
                              accept="image/*"
                              onChange={handleImageChange}
                              multiple
                            />
                          </Form.Group>
                          <div className="image-gallery d-flex flex-wrap gap-2 mt-2">
                            {Array.isArray(formData.images) &&
                              formData.images.map((image, index) => (
                                <div key={`gallery-${index}`} className="position-relative" style={{ width: "100px", height: "100px" }}>
                                  <img
                                    src={image}
                                    alt={`Gallery ${index}`}
                                    style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "4px" }}
                                  />
                                  <div
                                    className="position-absolute top-0 end-0 m-1 d-flex justify-content-center align-items-center remove-gallery-img-btn shadow-sm"
                                    style={{ width: "24px", height: "24px", borderRadius: "50%", zIndex: 1, cursor: "pointer" }}
                                    onClick={() => removeGalleryImage(index)}
                                    title="Remove Image"
                                    role="button"
                                  >
                                    <i className="fas fa-times" style={{ fontSize: "14px", margin: 0, padding: 0, lineHeight: 1, display: "flex" }}></i>
                                  </div>
                                </div>
                              ))}
                            {imagePreviews.map((preview, index) => (
                              <div key={`preview-${index}`} className="position-relative">
                                <img
                                  src={preview}
                                  alt={`Preview ${index}`}
                                  style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "4px", opacity: 0.7 }}
                                />
                                <div className="position-absolute w-100 h-100 top-0 start-0 d-flex justify-content-center align-items-center">
                                  <Spinner animation="border" variant="light" size="sm" />
                                </div>
                              </div>
                            ))}
                          </div>
                          <Row>
                            <Col>
                              <div className="auth mt-4">
                                <Button
                                  className=""
                                  variant="secondary"
                                  onClick={prevStep}
                                >
                                  Previous
                                </Button>
                                {initialData && initialData.listingId && (
                                  <Button
                                    variant="success"
                                    className="mx-2"
                                    onClick={saveFormData}
                                    disabled={loading}
                                  >
                                    {loading ? (
                                      <>
                                        <Spinner
                                          as="span"
                                          animation="border"
                                          size="sm"
                                          role="status"
                                          aria-hidden="true"
                                        />
                                        <span className="ms-1">Saving...</span>
                                      </>
                                    ) : (
                                      "Update"
                                    )}
                                  </Button>
                                )}
                                <Button
                                  className="mx-2"
                                  variant="btn btn-primary"
                                  onClick={nextStep}
                                >
                                  Next
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                      {step === 4 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>3. Departure Location & Area</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12}>
                              <h5>3.1 Departure Point</h5>
                            </Col>
                            <Col xs={12} className="mb-3">
                              <Form.Label>Boat Location*</Form.Label>
                              <div className="locationDiv">
                                <PlacesAutocomplete
                                  value={storageAddress}
                                  onChange={setStorageAddress}
                                  onSelect={handlesStorageSelect}
                                >
                                  {({
                                    getInputProps,
                                    suggestions,
                                    getSuggestionItemProps,
                                    loading,
                                  }) => (
                                    <div>
                                      <div className="input-group">
                                        <input
                                          {...getInputProps({
                                            placeholder: "Search Places...",
                                            className: "location-search-input form-control",
                                            name: "location",
                                          })}
                                        />
                                      </div>
                                      <div className="autocomplete-dropdown-container">
                                        {loading && <div>Loading...</div>}
                                        {suggestions.map((suggestion) => {
                                          const className = suggestion.active ? "suggestion-item--active" : "suggestion-item";
                                          return (
                                            <div {...getSuggestionItemProps(suggestion, { className: "location-suggestion form-control" })}>
                                              <span>{suggestion.description}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </PlacesAutocomplete>
                              </div>
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Marina / Dock / Ramp Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.businessInfo.marinaName}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.marinaName", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2 d-none">
                              <Form.Label>Street Address / Slip Number</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.businessInfo.streetAddress}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.streetAddress", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2 d-none">
                              <Form.Label>ZIP / Postal Code</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.businessInfo.zipCode}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.zipCode", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                          </Row>

                          <Row className="mb-3 mt-4">
                            <Col xs={12}>
                              <h5>3.2 Meeting Instructions (Shown to Customer)</h5>
                            </Col>
                            <Col xs={12} className="mb-2">
                              <Form.Label>Where exactly do guests meet you?</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.fishingGuideDetails.businessInfo.meetingInstructions}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.meetingInstructions", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} className="mb-2">
                              <Form.Label>Parking / Gate / Check-in Instructions</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.fishingGuideDetails.businessInfo.parkingInstructions}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.businessInfo.parkingInstructions", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button
                                className="mx-2"
                                variant="primary"
                                onClick={() => storageAddress.trim().length > 0 ? nextStep() : toast.error("Boat Location is required.")}
                              >
                                Next
                              </Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {step === 5 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>4. Vessel / Boat Details</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12}>
                              <h5>4.1 Basic Boat Info</h5>
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Boat Make / Brand</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.make}
                                onChange={handleChange}
                                name="make"
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Boat Model</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.model}
                                onChange={handleChange}
                                name="model"
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={4} className="mb-2">
                              <Form.Label>Year Built</Form.Label>
                              <Form.Control
                                type="number"
                                value={formData.year}
                                onChange={handleChange}
                                name="year"
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={4} className="mb-2">
                              <Form.Label>Length (ft)</Form.Label>
                              <Form.Control
                                type="number"
                                value={formData.boatLength || ""}
                                onChange={handleChange}
                                name="boatLength"
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={4} className="mb-2">
                              <Form.Label>Motor Type</Form.Label>
                              <Form.Control
                                as="select"
                                value={formData.engineType}
                                onChange={handleChange}
                                name="engineType"
                                className="form-control"
                              >
                                <option value="">Select Type</option>
                                <option value="Outboard">Outboard</option>
                                <option value="Inboard">Inboard</option>
                                <option value="Inboard/Outboard">Inboard/Outboard</option>
                              </Form.Control>
                            </Col>
                          </Row>

                          <Row className="mb-3 mt-4">
                            <Col xs={12}>
                              <h5>4.2 Capacity & Comfort</h5>
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Maximum Number of Anglers / Passengers</Form.Label>
                              <Form.Control
                                type="number"
                                value={formData.capacity}
                                onChange={handleChange}
                                name="capacity"
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Check
                                type="checkbox"
                                label="Child-Friendly?"
                                checked={formData.fishingGuideDetails.childFriendly}
                                onChange={(e) =>
                                  handleNestedChange(
                                    "fishingGuideDetails.childFriendly",
                                    e.target.checked
                                  )
                                }
                              />
                            </Col>
                          </Row>

                          <Row className="mb-3 mt-4">
                            <Col xs={12}>
                              <h5>4.3 Features</h5>
                              <p className="text-muted small">
                                Please select any significant features for your watercraft.
                              </p>
                            </Col>
                            <Col xs={12} md={4} className="mb-3">
                              <div className="d-flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Feature Name"
                                  value={newCheckboxText}
                                  onChange={(e) =>
                                    setNewCheckboxText(e.target.value)
                                  }
                                  className="form-control"
                                />
                                <button
                                  className="btn btn-primary text-nowrap"
                                  onClick={(e) => handleAddCheckbox(e)}
                                >
                                  Add
                                </button>
                              </div>
                            </Col>
                            <Col xs={12}>
                              <ul className="ks-cboxtags">
                                {checkboxes.map((checkbox) => (
                                  <li
                                    key={checkbox.id}
                                    className={
                                      checkbox.checked ? "ks-selected" : ""
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      id={checkbox.id}
                                      value={checkbox.label}
                                      checked={checkbox.checked}
                                      onChange={() =>
                                        handleCheckboxChange(
                                          checkbox.id,
                                          checkbox.label
                                        )
                                      }
                                    />
                                    <label htmlFor={checkbox.id}>
                                      {checkbox.label}
                                    </label>
                                  </li>
                                ))}
                              </ul>

                              <div className="mt-4 mb-3">
                                <h6 className="fw-bold">Priority Features (Drag to Reorder)</h6>
                                <p className="text-muted small">
                                  The top 3 features in this list will be displayed on your listing card. Drag and drop to reorder.
                                </p>
                                <div style={{ border: '1px solid #f0f0f0', padding: '15px', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                                  {priorityFeatures.length === 0 ? (
                                    <p className="text-muted fst-italic mb-0">Select features above to see them here.</p>
                                  ) : (
                                    <DndContext
                                      sensors={sensors}
                                      collisionDetection={closestCenter}
                                      onDragEnd={handleDragEnd}
                                    >
                                      <SortableContext
                                        items={priorityFeatures}
                                        strategy={verticalListSortingStrategy}
                                      >
                                        {priorityFeatures.map(item => (
                                          <SortableItem key={item} id={item} />
                                        ))}
                                      </SortableContext>
                                    </DndContext>
                                  )}
                                </div>
                              </div>
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {step === 6 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>5. Fishing Experience & Trip Details</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12}>
                              <h5>5.1 Fishing Styles & Waters</h5>
                            </Col>
                            <Col xs={12}>
                              <div className="d-flex flex-wrap gap-3">
                                {PREDEFINED_FISHING_STYLES.map((style) => (
                                  <Form.Check
                                    key={style}
                                    type="checkbox"
                                    label={style}
                                    checked={formData.fishingGuideDetails.fishingExperience.styles?.includes(style)}
                                    onChange={(e) => {
                                      const current = formData.fishingGuideDetails.fishingExperience.styles || [];
                                      const updated = e.target.checked ? [...current, style] : current.filter(s => s !== style);
                                      handleNestedChange("fishingGuideDetails.fishingExperience.styles", updated);
                                    }}
                                  />
                                ))}
                                {/* Render Custom Styles */}
                                {formData.fishingGuideDetails.fishingExperience.styles?.filter(s => !PREDEFINED_FISHING_STYLES.includes(s)).map((style) => (
                                  <div key={style} className="d-flex align-items-center">
                                    <Form.Check
                                      type="checkbox"
                                      label={style}
                                      checked={true}
                                      onChange={() => {
                                        const current = formData.fishingGuideDetails.fishingExperience.styles || [];
                                        handleNestedChange("fishingGuideDetails.fishingExperience.styles", current.filter(s => s !== style));
                                      }}
                                    />
                                  </div>
                                ))}
                                {!showCustomStyleInput ? (
                                  <i className="fa-regular fa-square-plus" style={{ fontSize: '24px', cursor: 'pointer', color: 'black' }} onClick={() => setShowCustomStyleInput(true)}></i>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                                      <Form.Control
                                        placeholder="e.g. Night Fishing"
                                        value={newStyle}
                                        onChange={(e) => setNewStyle(e.target.value)}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCustomOption("styles", newStyle, setNewStyle, setShowCustomStyleInput, "styles");
                                          }
                                        }}
                                        autoFocus
                                      />
                                      <Button variant="dark" style={{ backgroundColor: '#000000', borderColor: '#000000' }} onClick={() => handleAddCustomOption("styles", newStyle, setNewStyle, setShowCustomStyleInput, "styles")}>Add</Button>
                                    </InputGroup>
                                    <i className="fa-regular fa-square-minus text-danger" style={{ fontSize: '24px', cursor: 'pointer' }} onClick={() => { setShowCustomStyleInput(false); setNewStyle(""); }}></i>
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>

                          <Row className="mb-3 mt-4">
                            <Col xs={12}>
                              <h5>5.2 Target Species (Tick common ones)</h5>
                            </Col>
                            <Col xs={12}>
                              <div className="d-flex flex-wrap gap-3">
                                {PREDEFINED_TARGET_SPECIES.map((specie) => (
                                  <Form.Check
                                    key={specie}
                                    type="checkbox"
                                    label={specie}
                                    checked={formData.fishingGuideDetails.fishingExperience.species?.includes(specie)}
                                    onChange={(e) => {
                                      const current = formData.fishingGuideDetails.fishingExperience.species || [];
                                      const updated = e.target.checked ? [...current, specie] : current.filter(s => s !== specie);
                                      handleNestedChange("fishingGuideDetails.fishingExperience.species", updated);
                                    }}
                                  />
                                ))}
                                {/* Render Custom Species */}
                                {formData.fishingGuideDetails.fishingExperience.species?.filter(s => !PREDEFINED_TARGET_SPECIES.includes(s)).map((specie) => (
                                  <div key={specie} className="d-flex align-items-center">
                                    <Form.Check
                                      type="checkbox"
                                      label={specie}
                                      checked={true}
                                      onChange={() => {
                                        const current = formData.fishingGuideDetails.fishingExperience.species || [];
                                        handleNestedChange("fishingGuideDetails.fishingExperience.species", current.filter(s => s !== specie));
                                      }}
                                    />
                                  </div>
                                ))}
                                {!showCustomSpeciesInput ? (
                                  <i className="fa-regular fa-square-plus" style={{ fontSize: '24px', cursor: 'pointer', color: 'black' }} onClick={() => setShowCustomSpeciesInput(true)}></i>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                                      <Form.Control
                                        placeholder="e.g. Catfish"
                                        value={newSpecies}
                                        onChange={(e) => setNewSpecies(e.target.value)}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCustomOption("species", newSpecies, setNewSpecies, setShowCustomSpeciesInput, "species");
                                          }
                                        }}
                                        autoFocus
                                      />
                                      <Button variant="dark" style={{ backgroundColor: '#000000', borderColor: '#000000' }} onClick={() => handleAddCustomOption("species", newSpecies, setNewSpecies, setShowCustomSpeciesInput, "species")}>Add</Button>
                                    </InputGroup>
                                    <i className="fa-regular fa-square-minus text-danger" style={{ fontSize: '24px', cursor: 'pointer' }} onClick={() => { setShowCustomSpeciesInput(false); setNewSpecies(""); }}></i>
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>

                          <Row className="mb-3 mt-4">
                            <Col xs={12}>
                              <h5>5.3 Techniques Used</h5>
                            </Col>
                            <Col xs={12}>
                              <div className="d-flex flex-wrap gap-3">
                                {PREDEFINED_TECHNIQUES.map((tech) => (
                                  <Form.Check
                                    key={tech}
                                    type="checkbox"
                                    label={tech}
                                    checked={formData.fishingGuideDetails.fishingExperience.techniques?.includes(tech)}
                                    onChange={(e) => {
                                      const current = formData.fishingGuideDetails.fishingExperience.techniques || [];
                                      const updated = e.target.checked ? [...current, tech] : current.filter(t => t !== tech);
                                      handleNestedChange("fishingGuideDetails.fishingExperience.techniques", updated);
                                    }}
                                  />
                                ))}
                                {/* Render Custom Techniques */}
                                {formData.fishingGuideDetails.fishingExperience.techniques?.filter(t => !PREDEFINED_TECHNIQUES.includes(t)).map((tech) => (
                                  <div key={tech} className="d-flex align-items-center">
                                    <Form.Check
                                      type="checkbox"
                                      label={tech}
                                      checked={true}
                                      onChange={() => {
                                        const current = formData.fishingGuideDetails.fishingExperience.techniques || [];
                                        handleNestedChange("fishingGuideDetails.fishingExperience.techniques", current.filter(t => t !== tech));
                                      }}
                                    />
                                  </div>
                                ))}
                                {!showCustomTechniqueInput ? (
                                  <i className="fa-regular fa-square-plus" style={{ fontSize: '24px', cursor: 'pointer', color: 'black' }} onClick={() => setShowCustomTechniqueInput(true)}></i>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                                      <Form.Control
                                        placeholder="e.g. Kite Fishing"
                                        value={newTechnique}
                                        onChange={(e) => setNewTechnique(e.target.value)}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCustomOption("techniques", newTechnique, setNewTechnique, setShowCustomTechniqueInput, "techniques");
                                          }
                                        }}
                                        autoFocus
                                      />
                                      <Button variant="dark" style={{ backgroundColor: '#000000', borderColor: '#000000' }} onClick={() => handleAddCustomOption("techniques", newTechnique, setNewTechnique, setShowCustomTechniqueInput, "techniques")}>Add</Button>
                                    </InputGroup>
                                    <i className="fa-regular fa-square-minus text-danger" style={{ fontSize: '24px', cursor: 'pointer' }} onClick={() => { setShowCustomTechniqueInput(false); setNewTechnique(""); }}></i>
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {step === 7 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>6. What’s Included / Not Included</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12}>
                              <h5>6.1 Included in Price</h5>
                            </Col>
                            <Col xs={12}>
                              <div className="d-flex flex-wrap gap-3">
                                {PREDEFINED_INCLUDED_ITEMS.map((item) => (
                                  <Form.Check
                                    key={item}
                                    type="checkbox"
                                    label={item}
                                    checked={formData.fishingGuideDetails.whatsIncluded.included?.includes(item)}
                                    onChange={(e) => {
                                      const current = formData.fishingGuideDetails.whatsIncluded.included || [];
                                      const updated = e.target.checked ? [...current, item] : current.filter(i => i !== item);
                                      handleNestedChange("fishingGuideDetails.whatsIncluded.included", updated);
                                    }}
                                  />
                                ))}
                                {/* Render Custom Included Items */}
                                {formData.fishingGuideDetails.whatsIncluded.included?.filter(i => !PREDEFINED_INCLUDED_ITEMS.includes(i)).map((item) => (
                                  <div key={item} className="d-flex align-items-center">
                                    <Form.Check
                                      type="checkbox"
                                      label={item}
                                      checked={true}
                                      onChange={() => {
                                        const current = formData.fishingGuideDetails.whatsIncluded.included || [];
                                        handleNestedChange("fishingGuideDetails.whatsIncluded.included", current.filter(i => i !== item));
                                      }}
                                    />
                                  </div>
                                ))}
                                {!showCustomIncludedInput ? (
                                  <i className="fa-regular fa-square-plus" style={{ fontSize: '24px', cursor: 'pointer', color: 'black' }} onClick={() => setShowCustomIncludedInput(true)}></i>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                                      <Form.Control
                                        placeholder="e.g. Captain"
                                        value={newIncludedItem}
                                        onChange={(e) => setNewIncludedItem(e.target.value)}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCustomIncludedOption(newIncludedItem, setNewIncludedItem, setShowCustomIncludedInput);
                                          }
                                        }}
                                        autoFocus
                                      />
                                      <Button variant="dark" style={{ backgroundColor: '#000000', borderColor: '#000000' }} onClick={() => handleAddCustomIncludedOption(newIncludedItem, setNewIncludedItem, setShowCustomIncludedInput)}>Add</Button>
                                    </InputGroup>
                                    <i className="fa-regular fa-square-minus text-danger" style={{ fontSize: '24px', cursor: 'pointer' }} onClick={() => { setShowCustomIncludedInput(false); setNewIncludedItem(""); }}></i>
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>

                          <Row className="mb-3 mt-4">
                            <Col xs={12}>
                              <h5>6.2 Not Included</h5>
                            </Col>
                            <Col xs={12}>
                              <div className="d-flex flex-wrap gap-3">
                                {PREDEFINED_EXCLUDED_ITEMS.map((item) => (
                                  <Form.Check
                                    key={item}
                                    type="checkbox"
                                    label={item}
                                    checked={formData.fishingGuideDetails.whatsIncluded.notIncluded.excluded?.includes(item)}
                                    onChange={(e) => {
                                      const current = formData.fishingGuideDetails.whatsIncluded.notIncluded.excluded || [];
                                      const updated = e.target.checked ? [...current, item] : current.filter(i => i !== item);
                                      handleNestedChange("fishingGuideDetails.whatsIncluded.notIncluded.excluded", updated);
                                    }}
                                  />
                                ))}
                                {/* Render Custom Excluded Items */}
                                {formData.fishingGuideDetails.whatsIncluded.notIncluded.excluded?.filter(i => !PREDEFINED_EXCLUDED_ITEMS.includes(i)).map((item) => (
                                  <div key={item} className="d-flex align-items-center">
                                    <Form.Check
                                      type="checkbox"
                                      label={item}
                                      checked={true}
                                      onChange={() => {
                                        const current = formData.fishingGuideDetails.whatsIncluded.notIncluded.excluded || [];
                                        handleNestedChange("fishingGuideDetails.whatsIncluded.notIncluded.excluded", current.filter(i => i !== item));
                                      }}
                                    />
                                  </div>
                                ))}
                                {!showCustomExcludedInput ? (
                                  <i className="fa-regular fa-square-plus" style={{ fontSize: '24px', cursor: 'pointer', color: 'black' }} onClick={() => setShowCustomExcludedInput(true)}></i>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                                      <Form.Control
                                        placeholder="e.g. Gratuity not included"
                                        value={newExcludedItem}
                                        onChange={(e) => setNewExcludedItem(e.target.value)}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCustomExcludedOption(newExcludedItem, setNewExcludedItem, setShowCustomExcludedInput);
                                          }
                                        }}
                                        autoFocus
                                      />
                                      <Button variant="dark" style={{ backgroundColor: '#000000', borderColor: '#000000' }} onClick={() => handleAddCustomExcludedOption(newExcludedItem, setNewExcludedItem, setShowCustomExcludedInput)}>Add</Button>
                                    </InputGroup>
                                    <i className="fa-regular fa-square-minus text-danger" style={{ fontSize: '24px', cursor: 'pointer' }} onClick={() => { setShowCustomExcludedInput(false); setNewExcludedItem(""); }}></i>
                                  </div>
                                )}
                              </div>
                            </Col>
                            <Col xs={12} className="mb-2 mt-3">
                              <Form.Check
                                type="checkbox"
                                label="Recommended Gratuity Amount (Tips for Captain/Crew) % "
                                checked={formData.fishingGuideDetails.whatsIncluded.notIncluded?.gratuityExpected}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.whatsIncluded.notIncluded.gratuityExpected", e.target.checked)}
                              />
                              {formData.fishingGuideDetails.whatsIncluded.notIncluded?.gratuityExpected && (
                                <Form.Control
                                  type="text"
                                  placeholder="Recommended % or amount (e.g. 15-20%)"
                                  value={formData.fishingGuideDetails.whatsIncluded.notIncluded?.recommendedGratuity}
                                  onChange={(e) => handleNestedChange("fishingGuideDetails.whatsIncluded.notIncluded.recommendedGratuity", e.target.value)}
                                  className="form-control mt-2"
                                />
                              )}
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {step === 8 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>7. Trip Packages & Pricing</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-4">
                            <Col xs={12}>
                              <h5 className="mb-2">Standard Pricing</h5>
                              {/* --- Trips Starting From --- */}
                              <div className="p-3 mb-4 rounded border" style={{ backgroundColor: "#f8f9fa" }}>
                                <h6 className="mb-1" style={{ fontWeight: "600" }}>Trips Starting From</h6>
                                <p className="text-muted mb-2" style={{ fontSize: "13px" }}>
                                  Choose your lowest hourly or package price to advertise to renters
                                </p>
                                <Row>
                                  <Form.Group as={Col} xs={12} md={4}>
                                    <Form.Label>Starting Price ($)</Form.Label>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      placeholder="e.g. 299"
                                      value={pricing.tripsStartingFrom || ""}
                                      onChange={(e) =>
                                        setPricing({ ...pricing, tripsStartingFrom: e.target.value })
                                      }
                                    />
                                  </Form.Group>
                                </Row>
                              </div>
                              {/* --- Standard Pricing --- */}
                              <Row className="mb-3">
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>Hours</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="hours"
                                    value={pricing.hours}
                                    onChange={(e) =>
                                      setPricing({
                                        ...pricing,
                                        hours: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                  />
                                </Form.Group>
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>Price</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="hourlyRate"
                                    value={pricing.hourlyRate}
                                    onChange={(e) =>
                                      setPricing({
                                        ...pricing,
                                        hourlyRate: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                    min={1}
                                    required
                                  />
                                </Form.Group>
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>Max Passengers at this price</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="maxPassengers"
                                    value={pricing.maxPassengers}
                                    onChange={(e) =>
                                      setPricing({
                                        ...pricing,
                                        maxPassengers: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                    min={1}
                                    required
                                  />
                                </Form.Group>
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>Additional passengers allowed</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="additionalPassengersAllowed"
                                    value={pricing.additionalPassengersAllowed}
                                    onChange={(e) =>
                                      setPricing({
                                        ...pricing,
                                        additionalPassengersAllowed: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                  />
                                </Form.Group>
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>Amount added for each additional passenger</Form.Label>
                                  <InputGroup>
                                    <Form.Select
                                      value={pricing.additionalPassengerPriceType || "percentage"}
                                      onChange={(e) =>
                                        setPricing({ ...pricing, additionalPassengerPriceType: e.target.value })
                                      }
                                      style={{ maxWidth: "80px" }}
                                    >
                                      <option value="percentage">%</option>
                                      <option value="flat">$</option>
                                    </Form.Select>
                                    <Form.Control
                                      type="number"
                                      name="additionalPassengerPercentage"
                                      value={pricing.additionalPassengerPercentage}
                                      onChange={(e) =>
                                        setPricing({
                                          ...pricing,
                                          additionalPassengerPercentage: e.target.value,
                                        })
                                      }
                                      className="form-control"
                                    />
                                  </InputGroup>
                                </Form.Group>
                              </Row>

                              <h6 className="mb-2 mt-3">Hourly Options</h6>
                              {pricing.hourlyOptions.map((option, index) => (
                                <div key={index} className="mb-4 p-3 border rounded">
                                  <Row className="mb-3">
                                    <Col xs={12} md={6}>
                                      <Form.Label>Hours</Form.Label>
                                      <Form.Control
                                        type="number"
                                        name="hours"
                                        value={option.hours}
                                        onChange={(e) =>
                                          handleHourlyOptionChange(index, e)
                                        }
                                        className="form-control"
                                        required
                                      />
                                    </Col>
                                    <Col xs={12} md={6}>
                                      <Form.Label>Price</Form.Label>
                                      <Form.Control
                                        type="number"
                                        name="price"
                                        value={option.price}
                                        onChange={(e) =>
                                          handleHourlyOptionChange(index, e)
                                        }
                                        className="form-control"
                                        required
                                      />
                                    </Col>
                                  </Row>
                                  <Row className="mb-3">
                                    <Col xs={12} md={6}>
                                      <Form.Label>Max Passengers at this price</Form.Label>
                                      <Form.Control
                                        type="number"
                                        name="maxPassengers"
                                        value={option.maxPassengers}
                                        onChange={(e) =>
                                          handleHourlyOptionChange(index, e)
                                        }
                                        className="form-control"
                                        required
                                      />
                                    </Col>
                                    <Col xs={12} md={6}>
                                      <Form.Label>Additional passengers allowed</Form.Label>
                                      <Form.Control
                                        type="number"
                                        name="additionalPassengersAllowed"
                                        value={option.additionalPassengersAllowed}
                                        onChange={(e) =>
                                          handleHourlyOptionChange(index, e)
                                        }
                                        className="form-control"
                                        required
                                      />
                                    </Col>
                                  </Row>
                                  <Row className="mb-3">
                                    <Col xs={12} md={6}>
                                      <Form.Label>Amount added for each additional passenger</Form.Label>
                                      <InputGroup>
                                        <Form.Select
                                          value={option.additionalPassengerPriceType || "percentage"}
                                          onChange={(e) => {
                                            const newOptions = [...pricing.hourlyOptions];
                                            newOptions[index] = { ...newOptions[index], additionalPassengerPriceType: e.target.value };
                                            setPricing({ ...pricing, hourlyOptions: newOptions });
                                          }}
                                          style={{ maxWidth: '80px' }}
                                        >
                                          <option value="percentage">%</option>
                                          <option value="flat">$</option>
                                        </Form.Select>
                                        <Form.Control
                                          type="number"
                                          name="additionalPassengerPercentage"
                                          value={option.additionalPassengerPercentage}
                                          onChange={(e) =>
                                            handleHourlyOptionChange(index, e)
                                          }
                                          className="form-control"
                                          required
                                        />
                                      </InputGroup>
                                    </Col>
                                    <Col
                                      xs={12} md={6}
                                      className="d-flex justify-content-end align-items-end mt-2"
                                    >
                                      <Button
                                        variant="danger"
                                        onClick={() =>
                                          removeHourlyOption(index)
                                        }
                                      >
                                        Remove
                                      </Button>
                                    </Col>
                                  </Row>
                                </div>
                              ))}
                              <Button onClick={addHourlyOption} className="mb-4">
                                Add Hourly Option
                              </Button>

                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <h6 className="mb-2">Overnight Options</h6>
                                <Button
                                  variant={
                                    overnightEnabled ? "danger" : "primary"
                                  }
                                  onClick={toggleOvernight}
                                >
                                  {overnightEnabled ? "Disable" : "Enable"}{" "}
                                  Overnight Options
                                </Button>
                              </div>

                              {pricing.dailyOptions.map((option, index) => (
                                <Row key={index} className="mb-3">
                                  <Col xs={12} md={6}>
                                    <Form.Label>Days</Form.Label>
                                    <Form.Control
                                      type="number"
                                      name="days"
                                      disabled={!overnightEnabled}
                                      value={option.days}
                                      onChange={(e) =>
                                        handleDailyOptionChange(index, e)
                                      }
                                      className="form-control"
                                      required
                                    />
                                  </Col>
                                  <Col xs={12} md={6}>
                                    <Form.Label>Price</Form.Label>
                                    <Form.Control
                                      type="number"
                                      name="price"
                                      disabled={!overnightEnabled}
                                      value={option.price}
                                      onChange={(e) =>
                                        handleDailyOptionChange(index, e)
                                      }
                                      className="form-control"
                                      required
                                    />
                                  </Col>
                                  <Col
                                    xs={12}
                                    className="d-flex justify-content-end mt-2"
                                  >
                                    <Button
                                      variant="danger"
                                      onClick={() => removeDailyOption(index)}
                                    >
                                      Remove
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                              <Button onClick={addDailyOption} className="mb-4">
                                Add Daily Option
                              </Button>
                            </Col>
                          </Row>

                          <Row className="mb-4">
                            <Col xs={12}>
                              <h5 className="mb-2">Trip Packages</h5>
                            </Col>
                          </Row>

                          {formData.fishingGuideDetails.tripPackages.map((pkg, index) => (
                            <div key={index} className="trip-package-box p-3 mb-4 rounded border">
                              <Row className="mb-3">
                                <Col xs={12} className="d-flex justify-content-between align-items-center">
                                  <h5>Package {index + 1}</h5>
                                  <div className="d-flex gap-2">
                                    <Button variant="outline-primary" size="sm" onClick={() => {
                                      const packageToDuplicate = formData.fishingGuideDetails.tripPackages[index];
                                      const duplicatedPackage = JSON.parse(JSON.stringify(packageToDuplicate));
                                      // Modify the name to indicate it's a copy
                                      duplicatedPackage.name = packageToDuplicate.name ? `${packageToDuplicate.name} (Copy)` : "";
                                      const updated = [...formData.fishingGuideDetails.tripPackages];
                                      updated.splice(index + 1, 0, duplicatedPackage);
                                      handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                    }}>Duplicate</Button>
                                    {formData.fishingGuideDetails.tripPackages.length > 1 && (
                                      <Button variant="outline-danger" size="sm" onClick={() => {
                                        const updated = [...formData.fishingGuideDetails.tripPackages];
                                        updated.splice(index, 1);
                                        handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                      }}>Remove Package</Button>
                                    )}
                                  </div>
                                </Col>
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Package Name</Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder="e.g. Half Day Morning Inshore"
                                    value={pkg.name}
                                    onChange={(e) => {
                                      const updated = [...formData.fishingGuideDetails.tripPackages];
                                      updated[index] = { ...updated[index], name: e.target.value };
                                      handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                    }}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Duration (hours)</Form.Label>
                                  <Form.Control
                                    type="text"
                                    placeholder="e.g. 4 Hours"
                                    value={pkg.duration}
                                    onChange={(e) => {
                                      const updated = [...formData.fishingGuideDetails.tripPackages];
                                      updated[index] = { ...updated[index], duration: e.target.value };
                                      handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                    }}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Base Price (Flat Trip Price)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={pkg.basePrice}
                                    onChange={(e) => {
                                      const updated = [...formData.fishingGuideDetails.tripPackages];
                                      updated[index] = { ...updated[index], basePrice: e.target.value };
                                      handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                    }}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Max Passengers for this Price</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={pkg.maxGuests}
                                    onChange={(e) => {
                                      const updated = [...formData.fishingGuideDetails.tripPackages];
                                      updated[index] = { ...updated[index], maxGuests: e.target.value };
                                      handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                    }}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Additional passengers allowed</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={pkg.priceChangeOptions?.afterGuests || ""}
                                    placeholder="e.g. 2"
                                    onChange={(e) => {
                                      const updated = [...formData.fishingGuideDetails.tripPackages];
                                      const currentOptions = updated[index].priceChangeOptions || { type: "Flat Trip Price Only", extraPersonPrice: "", afterGuests: "" };
                                      updated[index] = {
                                        ...updated[index],
                                        priceChangeOptions: {
                                          ...currentOptions,
                                          afterGuests: e.target.value
                                        }
                                      };
                                      handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                    }}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Amount added for each additional passenger</Form.Label>
                                  <InputGroup>
                                    <Form.Select
                                      value={pkg.priceChangeOptions?.extraPersonPriceType || "percentage"}
                                      onChange={(e) => {
                                        const updated = [...formData.fishingGuideDetails.tripPackages];
                                        const currentOptions = updated[index].priceChangeOptions || { type: "Flat Trip Price Only", extraPersonPrice: "", afterGuests: "", extraPersonPriceType: "percentage" };
                                        updated[index] = {
                                          ...updated[index],
                                          priceChangeOptions: {
                                            ...currentOptions,
                                            extraPersonPriceType: e.target.value
                                          }
                                        };
                                        handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                      }}
                                      style={{ maxWidth: '80px' }}
                                    >
                                      <option value="percentage">%</option>
                                      <option value="flat">$</option>
                                    </Form.Select>
                                    <Form.Control
                                      type="number"
                                      value={pkg.priceChangeOptions?.extraPersonPrice || ""}
                                      placeholder="e.g. 10"
                                      onChange={(e) => {
                                        const updated = [...formData.fishingGuideDetails.tripPackages];
                                        const currentOptions = updated[index].priceChangeOptions || { type: "Flat Trip Price Only", extraPersonPrice: "", afterGuests: "", extraPersonPriceType: "percentage" };
                                        updated[index] = {
                                          ...updated[index],
                                          priceChangeOptions: {
                                            ...currentOptions,
                                            extraPersonPrice: e.target.value
                                          }
                                        };
                                        handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                      }}
                                      className="form-control"
                                    />
                                  </InputGroup>
                                </Col>

                                {/* Days Offered */}
                                <Col xs={12} className="mb-3 mt-3">
                                  <Form.Label>Days Offered</Form.Label>
                                  <div className="d-flex flex-wrap align-items-center gap-3">
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                                      <Form.Check
                                        key={day}
                                        type="checkbox"
                                        id={`day-${day}-${index}`}
                                        label={day}
                                        checked={pkg.daysOffered?.[day] || false}
                                        onChange={(e) => {
                                          const updated = [...formData.fishingGuideDetails.tripPackages];
                                          updated[index] = {
                                            ...updated[index],
                                            daysOffered: { ...updated[index].daysOffered, [day]: e.target.checked }
                                          };
                                          handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                        }}
                                        className="me-3 mb-0"
                                      />
                                    ))}
                                    
                                    <Button 
                                      variant="outline-dark" 
                                      size="sm" 
                                      style={{ borderRadius: '20px', width: '100px', fontSize: '12px' }}
                                      onClick={() => {
                                        const isAllSelected = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].every(day => pkg.daysOffered?.[day]);
                                        const updated = [...formData.fishingGuideDetails.tripPackages];
                                        const newDays = {};
                                        ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach(day => {
                                          newDays[day] = !isAllSelected;
                                        });
                                        updated[index] = { ...updated[index], daysOffered: newDays };
                                        handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                      }}
                                    >
                                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].every(day => pkg.daysOffered?.[day]) ? "Deselect All" : "Select All"}
                                    </Button>
                                  </div>
                                </Col>

                                {/* Start Time Options */}
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Start Time</Form.Label>
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopTimePicker
                                      value={pkg.startTimeOptions?.[0] ? dayjs(pkg.startTimeOptions[0], "h:mm A") : null}
                                      onChange={(newValue) => {
                                        const updated = [...formData.fishingGuideDetails.tripPackages];
                                        updated[index] = {
                                          ...updated[index],
                                          startTimeOptions: newValue ? [newValue.format("h:mm A")] : []
                                        };
                                        handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                      }}
                                      slotProps={{
                                        textField: {
                                          fullWidth: true,
                                          sx: {
                                            '& .MuiOutlinedInput-notchedOutline': {
                                              border: '1px solid rgba(0,0,0,0.23) !important'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                              borderColor: 'rgba(0,0,0,0.87) !important'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                              borderColor: '#1976d2 !important'
                                            }
                                          }
                                        }
                                      }}
                                    />
                                  </LocalizationProvider>
                                </Col>

                                {/* Available Date Range */}
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Available Date Range</Form.Label>
                                  <Row>
                                    <Col xs={6}>
                                      <Form.Control
                                        type="date"
                                        placeholder="Start Date"
                                        value={pkg.availableDateRange?.startDate || ''}
                                        onChange={(e) => {
                                          const updated = [...formData.fishingGuideDetails.tripPackages];
                                          updated[index] = {
                                            ...updated[index],
                                            availableDateRange: { ...updated[index].availableDateRange, startDate: e.target.value }
                                          };
                                          handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                        }}
                                        className="form-control"
                                      />
                                    </Col>
                                    <Col xs={6}>
                                      <Form.Control
                                        type="date"
                                        placeholder="End Date"
                                        value={pkg.availableDateRange?.endDate || ''}
                                        onChange={(e) => {
                                          const updated = [...formData.fishingGuideDetails.tripPackages];
                                          updated[index] = {
                                            ...updated[index],
                                            availableDateRange: { ...updated[index].availableDateRange, endDate: e.target.value }
                                          };
                                          handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                        }}
                                        className="form-control"
                                      />
                                    </Col>
                                  </Row>
                                  <Form.Text className="text-muted">Leave empty for year-round availability</Form.Text>
                                </Col>

                                {/* Book / Instant Book 3-State Switch */}
                                <Col xs={12} lg={8} className="mb-2 mt-3">
                                  <Form.Label className="d-block" style={{ fontSize: '14px', fontWeight: '500', color: '#212529' }}>Booking Mode</Form.Label>
                                  <ButtonGroup size="sm" className="w-100 booking-mode-group" style={{ maxWidth: '400px', backgroundColor: '#e9ecef', borderRadius: '30px', padding: '3px', border: 'none' }}>
                                    <ToggleButton
                                      id={`mode-off-${index}`}
                                      type="radio"
                                      variant="light"
                                      name={`booking-mode-${index}`}
                                      value="off"
                                      checked={!pkg.instantBook && !pkg.allowBooking}
                                      onChange={() => {
                                        const updated = [...formData.fishingGuideDetails.tripPackages];
                                        updated[index] = { ...updated[index], instantBook: false, allowBooking: false };
                                        handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                      }}
                                    >
                                      Disabled
                                    </ToggleButton>
                                    <ToggleButton
                                      id={`mode-book-${index}`}
                                      type="radio"
                                      variant="light"
                                      name={`booking-mode-${index}`}
                                      value="book"
                                      checked={!pkg.instantBook && pkg.allowBooking}
                                      onChange={() => {
                                        const updated = [...formData.fishingGuideDetails.tripPackages];
                                        updated[index] = { ...updated[index], instantBook: false, allowBooking: true };
                                        handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                      }}
                                    >
                                      Book
                                    </ToggleButton>
                                    <ToggleButton
                                      id={`mode-instant-${index}`}
                                      type="radio"
                                      variant="light"
                                      name={`booking-mode-${index}`}
                                      value="instant"
                                      checked={pkg.instantBook}
                                      onChange={() => {
                                        const updated = [...formData.fishingGuideDetails.tripPackages];
                                        updated[index] = { ...updated[index], instantBook: true, allowBooking: false };
                                        handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                      }}
                                    >
                                      Instant Book
                                    </ToggleButton>
                                  </ButtonGroup>
                                  <Form.Text className="text-muted d-block mt-1">Select how customers can book this package</Form.Text>
                                </Col>
                                <Col xs={12} lg={4} className="mb-2 mt-3 d-flex flex-column justify-content-center">
                                  <Form.Check
                                    type="switch"
                                    id={`free-cancellation-${index}`}
                                    label="Free Cancellation"
                                    checked={pkg.freeCancellation || false}
                                    onChange={(e) => {
                                      const updated = [...formData.fishingGuideDetails.tripPackages];
                                      updated[index] = { ...updated[index], freeCancellation: e.target.checked };
                                      handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                    }}
                                  />
                                  <Form.Text className="text-muted">Offer free cancellation for this package</Form.Text>
                                </Col>

                                {/* Description */}
                                <Col xs={12} className="mb-2 mt-2">
                                  <Form.Label>Package Description (Optional)</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Describe what's included in this package..."
                                    value={pkg.description || ''}
                                    onChange={(e) => {
                                      const updated = [...formData.fishingGuideDetails.tripPackages];
                                      updated[index] = { ...updated[index], description: e.target.value };
                                      handleNestedChange("fishingGuideDetails.tripPackages", updated);
                                    }}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                            </div>
                          ))}

                          <Button variant="outline-primary" onClick={() => {
                            const newPackage = {
                              name: "",
                              duration: "",
                              startTimeOptions: [],
                              basePrice: "",
                              maxGuests: "",
                              priceChangeOptions: { type: "Flat Trip Price Only" },
                              daysOffered: { Mon: true, Tue: true, Wed: true, Thu: true, Fri: true, Sat: true, Sun: true },
                              description: "",
                              allowBooking: true, // Default to standard booking inquiry
                              instantBook: false,
                              freeCancellation: false,
                              availableDateRange: { startDate: "", endDate: "" }
                            };
                            handleNestedChange("fishingGuideDetails.tripPackages", [...formData.fishingGuideDetails.tripPackages, newPackage]);
                          }}>+ Add Another Trip Package</Button>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {step === 9 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>8. Calendar & Booking Rules</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Lead Time / Cut-off Time</Form.Label>
                              <Form.Control
                                as="select"
                                value={formData.fishingGuideDetails.calendarRules?.leadTime}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.calendarRules.leadTime", e.target.value)}
                                className="form-control"
                              >
                                <option value="">Select Cut-off</option>
                                <option value="2 hours">2 hours before</option>
                                <option value="6 hours">6 hours before</option>
                                <option value="12 hours">12 hours before</option>
                                <option value="24 hours">24 hours before</option>
                                <option value="48 hours">48 hours before</option>
                              </Form.Control>
                            </Col>
                            <Col xs={12} md={6} className="mb-2 d-flex align-items-center">
                              <Form.Check
                                type="switch"
                                label="Enable Instant Book"
                                checked={formData.fishingGuideDetails.calendarRules?.instantBook}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.calendarRules.instantBook", e.target.checked)}
                              />
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {((step === 10 && formData.boatType === "Fishing Charter") || (step === 5 && formData.boatType !== "Fishing Charter")) && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>{formData.boatType === "Fishing Charter" ? "9. Policies & Deposit/Payments" : "5. Cancellation Policy"}</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12} className="mb-3">
                              <Form.Label className="fw-bold">Cancellation Policy</Form.Label>
                              <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
                                Select a cancellation policy for your listing. This determines how refunds are handled when renters cancel.
                              </p>

                              {/* Relaxed Policy */}
                              <div
                                className="p-3 mb-3"
                                style={{
                                  backgroundColor: formData.fishingGuideDetails.policies?.cancellation === "Relaxed" ? "#e8f4fd" : "#f8f9fa",
                                  border: formData.fishingGuideDetails.policies?.cancellation === "Relaxed" ? "2px solid #0d6efd" : "1px solid #dee2e6",
                                  borderRadius: "0.375rem",
                                  cursor: "pointer"
                                }}
                                onClick={() => handleNestedChange("fishingGuideDetails.policies.cancellation", "Relaxed")}
                              >
                                <Form.Check
                                  type="radio"
                                  id="cancellation-relaxed"
                                  name="cancellationPolicy"
                                  checked={formData.fishingGuideDetails.policies?.cancellation === "Relaxed"}
                                  onChange={() => handleNestedChange("fishingGuideDetails.policies.cancellation", "Relaxed")}
                                  label={
                                    <div>
                                      <strong style={{ fontSize: "16px" }}>Relaxed</strong>
                                      <p className="mb-1 mt-2" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>100%</strong> of the rental price if cancelled more than <strong>24 hours</strong> prior to commencement of the rental.
                                      </p>
                                      <p className="mb-0" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>50%</strong> of the rental price if cancelled within <strong>24 hours</strong> of commencement of the rental.
                                      </p>
                                    </div>
                                  }
                                />
                              </div>

                              {/* Regulated Policy */}
                              <div
                                className="p-3 mb-3"
                                style={{
                                  backgroundColor: formData.fishingGuideDetails.policies?.cancellation === "Regulated" ? "#e8f4fd" : "#f8f9fa",
                                  border: formData.fishingGuideDetails.policies?.cancellation === "Regulated" ? "2px solid #0d6efd" : "1px solid #dee2e6",
                                  borderRadius: "0.375rem",
                                  cursor: "pointer"
                                }}
                                onClick={() => handleNestedChange("fishingGuideDetails.policies.cancellation", "Regulated")}
                              >
                                <Form.Check
                                  type="radio"
                                  id="cancellation-regulated"
                                  name="cancellationPolicy"
                                  checked={formData.fishingGuideDetails.policies?.cancellation === "Regulated"}
                                  onChange={() => handleNestedChange("fishingGuideDetails.policies.cancellation", "Regulated")}
                                  label={
                                    <div>
                                      <strong style={{ fontSize: "16px" }}>Regulated</strong>
                                      <p className="mb-1 mt-2" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>100%</strong> of the rental price if cancelled more than <strong>7 days</strong> prior to commencement of the rental.
                                      </p>
                                      <p className="mb-1" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>75%</strong> of the rental price if cancelled less than <strong>7 days</strong> but more than <strong>48 hours</strong> prior to commencement of the rental.
                                      </p>
                                      <p className="mb-0" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>50%</strong> of the rental price if cancelled less than <strong>48 hours</strong> but more than <strong>24 hours</strong> prior to commencement of the rental.
                                      </p>
                                    </div>
                                  }
                                />
                              </div>

                              {/* Restricted Policy */}
                              <div
                                className="p-3 mb-3"
                                style={{
                                  backgroundColor: formData.fishingGuideDetails.policies?.cancellation === "Restricted" ? "#e8f4fd" : "#f8f9fa",
                                  border: formData.fishingGuideDetails.policies?.cancellation === "Restricted" ? "2px solid #0d6efd" : "1px solid #dee2e6",
                                  borderRadius: "0.375rem",
                                  cursor: "pointer"
                                }}
                                onClick={() => handleNestedChange("fishingGuideDetails.policies.cancellation", "Restricted")}
                              >
                                <Form.Check
                                  type="radio"
                                  id="cancellation-restricted"
                                  name="cancellationPolicy"
                                  checked={formData.fishingGuideDetails.policies?.cancellation === "Restricted"}
                                  onChange={() => handleNestedChange("fishingGuideDetails.policies.cancellation", "Restricted")}
                                  label={
                                    <div>
                                      <strong style={{ fontSize: "16px" }}>Restricted</strong>
                                      <p className="mb-1 mt-2" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>75%</strong> of the rental price if cancelled more than <strong>7 days</strong> prior to commencement of the rental.
                                      </p>
                                      <p className="mb-1" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>50%</strong> of the rental price if cancelled less than <strong>7 days</strong> but more than <strong>48 hours</strong> prior to commencement of the rental.
                                      </p>
                                      <p className="mb-0" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>25%</strong> of the rental price if cancelled less than <strong>48 hours</strong> but more than <strong>24 hours</strong> prior to commencement of the rental.
                                      </p>
                                    </div>
                                  }
                                />
                              </div>

                              {/* Free Cancellation Policy */}
                              <div
                                className="p-3 mb-3"
                                style={{
                                  backgroundColor: formData.fishingGuideDetails.policies?.cancellation === "Free" ? "#e8f4fd" : "#f8f9fa",
                                  border: formData.fishingGuideDetails.policies?.cancellation === "Free" ? "2px solid #0d6efd" : "1px solid #dee2e6",
                                  borderRadius: "0.375rem",
                                  cursor: "pointer"
                                }}
                                onClick={() => handleNestedChange("fishingGuideDetails.policies.cancellation", "Free")}
                              >
                                <Form.Check
                                  type="radio"
                                  id="cancellation-free"
                                  name="cancellationPolicy"
                                  checked={formData.fishingGuideDetails.policies?.cancellation === "Free"}
                                  onChange={() => handleNestedChange("fishingGuideDetails.policies.cancellation", "Free")}
                                  label={
                                    <div>
                                      <strong style={{ fontSize: "16px" }}>Free Cancellation</strong>
                                      <p className="mb-0 mt-2" style={{ fontSize: "14px", color: "#000000" }}>
                                        <strong>100%</strong> refund for all cancellations. No charges will be applied for any cancellation.
                                      </p>
                                    </div>
                                  }
                                />
                              </div>

                              <p className="text-muted mt-2" style={{ fontSize: "13px", fontStyle: "italic" }}>
                                Note: If no policy is selected, the default <a href="/cancellation" target="_blank" rel="noopener noreferrer">BBR policy</a> will apply. Transaction fees and applicable taxes remitted to taxing authorities prior to cancellation will not be automatically refunded.
                              </p>
                            </Col>
                          </Row>

                          {formData.boatType === "Fishing Charter" && (
                            <>
                              <Row className="mb-2">
                                <Col xs={12}>
                                  <p className="text-muted mb-0" style={{ fontSize: "13px", fontStyle: "italic" }}>
                                    Deposit % amount you want the renter to pay at checkout. Default minimum is 10%.
                                  </p>
                                </Col>
                              </Row>

                              <Row className="mb-3">
                                <Col xs={12} md={6} className="mb-2">
                                  <Form.Label>Deposit Percentage (%)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min="10"
                                    max="100"
                                    value={formData.fishingGuideDetails.policies?.paymentTerms?.depositPercentage}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow empty string or valid number within range (0-100) to allow typing "1" for "15"
                                      if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 100)) {
                                        handleNestedChange("fishingGuideDetails.policies.paymentTerms.depositPercentage", value);
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = parseInt(e.target.value);
                                      // Enforce min 10 on blur
                                      if (isNaN(value) || value < 10) {
                                        handleNestedChange("fishingGuideDetails.policies.paymentTerms.depositPercentage", "10");
                                      } else if (value > 100) {
                                        handleNestedChange("fishingGuideDetails.policies.paymentTerms.depositPercentage", "100");
                                      }
                                    }}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6} className="mb-2 d-flex align-items-end">
                                  <Form.Check
                                    type="switch"
                                    id="collectBalanceAtMeetup"
                                    label="Collect balance at meet-up location"
                                    checked={formData.fishingGuideDetails.policies?.paymentTerms?.collectBalanceAtMeetup || false}
                                    onChange={(e) => handleNestedChange("fishingGuideDetails.policies.paymentTerms.collectBalanceAtMeetup", e.target.checked)}
                                    className="mb-2"
                                  />
                                </Col>
                              </Row>

                              <Row className="mb-3">
                                <Col xs={12}>
                                  <div
                                    className="p-3"
                                    style={{
                                      backgroundColor: "#f8f9fa",
                                      border: "1px solid #dee2e6",
                                      borderRadius: "0.375rem",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {formData.fishingGuideDetails.policies?.paymentTerms?.collectBalanceAtMeetup ? (
                                      <p className="mb-0">
                                        <strong>Balance at Meet-Up:</strong> The renter will pay the remaining balance to you at the meet-up location at the start of the trip. The renter will be notified before completing checkout.
                                      </p>
                                    ) : (
                                      <p className="mb-0">
                                        <strong>BBR Collects Balance:</strong> BBR will collect the full remaining balance from the renter <strong>48 hours prior</strong> to the start of the trip. If the trip is within 48 hours, the full amount will be collected at checkout.
                                      </p>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            </>
                          )}

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              {formData.boatType === "Fishing Charter" ? (
                                <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                              ) : (
                                <Button className="mx-2" variant="primary" type="submit" disabled={loading}>
                                  {loading ? "Submitting..." : "Submit"}
                                </Button>
                              )}
                            </Col>
                          </Row>
                        </>
                      )}

                      {step === 11 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>10. Legal, Licenses & Insurance</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>Captain Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.legal?.captain?.fullName}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.legal.captain.fullName", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} md={6} className="mb-2">
                              <Form.Label>License Number</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.legal?.captain?.licenseNumber}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.legal.captain.licenseNumber", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                          </Row>

                          <Row className="mb-4">
                            <Col xs={12}>
                              <h5 className="mt-3">Fishing Charter Licenses</h5>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12}>
                              <Form.Check
                                type="checkbox"
                                id="sixPackLicense"
                                label="Check here to advertise you have a 6-Pack License (up to 6 passengers)"
                                checked={formData.fishingGuideDetails.legal?.licenses?.sixPackLicense}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.legal.licenses.sixPackLicense", e.target.checked)}
                                className="mb-3"
                              />

                              <p className="mb-3">To carry more than 6 passengers on a Fishing Charter you need MCL and COI:</p>

                              <Form.Check
                                type="checkbox"
                                id="masterCaptainLicense"
                                label="Check here to advertise you have a current Master Captain License"
                                checked={formData.fishingGuideDetails.legal?.licenses?.masterCaptainLicense}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.legal.licenses.masterCaptainLicense", e.target.checked)}
                                className="mb-3"
                              />

                              <Form.Check
                                type="checkbox"
                                id="coastGuardCOI"
                                label="Check here to advertise your vessel has a current Coast Guard Certificate of Inspection – COI"
                                checked={formData.fishingGuideDetails.legal?.licenses?.coastGuardCOI}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.legal.licenses.coastGuardCOI", e.target.checked)}
                                className="mb-3"
                              />

                              <Form.Check
                                type="checkbox"
                                id="stateLicensed9Miles"
                                label="Check here to advertise you are state licensed up to 9 miles offshore"
                                checked={formData.fishingGuideDetails.legal?.licenses?.stateLicensed9Miles}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.legal.licenses.stateLicensed9Miles", e.target.checked)}
                                className="mb-3"
                              />

                              <Form.Check
                                type="checkbox"
                                id="federallyLicensedOver9Miles"
                                label="Check here to advertise you are Federally licensed over 9 miles offshore"
                                checked={formData.fishingGuideDetails.legal?.licenses?.federallyLicensedOver9Miles}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.legal.licenses.federallyLicensedOver9Miles", e.target.checked)}
                                className="mb-3"
                              />

                              <Form.Check
                                type="checkbox"
                                id="fishingCharterInsurance"
                                label="Check here to advertise you have insurance to cover your fishing charter service as you described it"
                                checked={formData.fishingGuideDetails.legal?.licenses?.fishingCharterInsurance}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.legal.licenses.fishingCharterInsurance", e.target.checked)}
                                className="mb-4"
                              />

                              <div className="mt-4">
                                <h6>Upload a copy of your documents here</h6>
                                <Form.Group>
                                  <Form.Control
                                    type="file"
                                    name="licenseDocuments"
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    onChange={handleLicenseDocumentUpload}
                                    multiple
                                    className="mb-2"
                                  />
                                  <small className="text-muted">You can upload multiple documents (PDF, images, Word docs)</small>
                                </Form.Group>
                                {licenseDocumentPreviews.length > 0 && (
                                  <div className="mt-3">
                                    <p className="mb-2">Uploaded Documents:</p>
                                    <ul className="list-group">
                                      {licenseDocumentPreviews.map((doc, index) => (
                                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                          <span>{doc.name}</span>
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeLicenseDocument(index)}
                                          >
                                            Remove
                                          </Button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {step === 12 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>11. Photos & Media</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">

                            <Col xs={12} className="mb-3">
                              <Form.Group>
                                <Form.Label>Add Gallery Images</Form.Label>
                                <Form.Control
                                  type="file"
                                  name="images"
                                  accept="image/*"
                                  onChange={handleImageChange}
                                  multiple
                                />
                              </Form.Group>
                              <div className="image-gallery d-flex flex-wrap gap-2 mt-2">
                                {Array.isArray(formData.images) &&
                                  formData.images.map((image, index) => (
                                    <div key={`gallery-${index}`} className="position-relative" style={{ width: "100px", height: "100px" }}>
                                      <img
                                        src={image}
                                        alt={`Gallery ${index}`}
                                        style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "4px" }}
                                      />
                                      <div
                                        className="position-absolute top-0 end-0 m-1 d-flex justify-content-center align-items-center remove-gallery-img-btn shadow-sm"
                                        style={{ width: "24px", height: "24px", borderRadius: "50%", zIndex: 1, cursor: "pointer" }}
                                        onClick={() => removeGalleryImage(index)}
                                        title="Remove Image"
                                        role="button"
                                      >
                                        <i className="fas fa-times" style={{ fontSize: "14px", margin: 0, padding: 0, lineHeight: 1, display: "flex" }}></i>
                                      </div>
                                    </div>
                                  ))}
                                {imagePreviews.map((preview, index) => (
                                  <div key={`preview-${index}`} className="position-relative">
                                    <img
                                      src={preview}
                                      alt={`Preview ${index}`}
                                      style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "4px", opacity: 0.7 }}
                                    />
                                    <div className="position-absolute w-100 h-100 top-0 start-0 d-flex justify-content-center align-items-center">
                                      <Spinner animation="border" variant="light" size="sm" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Col>

                            <Col xs={12} className="mb-2">
                              <Form.Label>Promo Video URL (YouTube/Vimeo)</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.fishingGuideDetails.media?.promoVideoUrl}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.media.promoVideoUrl", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-3" variant="primary" onClick={nextStep}>Next</Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {step === 13 && formData.boatType === "Fishing Charter" && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12}>
                              <h4>12. House Rules & Fine Print</h4>
                              <hr />
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col xs={12} className="mb-2">
                              <Form.Label>Catch Policy (Keep/Release)</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={formData.fishingGuideDetails.houseRules?.catchPolicy}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.houseRules.catchPolicy", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                            <Col xs={12} className="mb-2">
                              <Form.Label>Welcome Message / Pre-Trip Instructions</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.fishingGuideDetails.messaging?.welcomeMessage}
                                onChange={(e) => handleNestedChange("fishingGuideDetails.messaging.welcomeMessage", e.target.value)}
                                className="form-control"
                              />
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <Button variant="secondary" onClick={prevStep}>Previous</Button>
                              {initialData && initialData.listingId && (
                                <Button
                                  variant="success"
                                  className="mx-2"
                                  onClick={saveFormData}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      <span className="ms-1">Saving...</span>
                                    </>
                                  ) : (
                                    "Update"
                                  )}
                                </Button>
                              )}
                              <Button className="mx-2" variant="primary" type="submit">Submit Listing</Button>
                            </Col>
                          </Row>
                        </>
                      )}
                      {step === 4 && formData.boatType !== "Fishing Charter" && (
                        <>
                          <Row className="mb-2">
                            <h5>Boat Specs*</h5>
                          </Row>
                          <Row className="mb-2">
                            <Col xs={12} md={6}>
                              <Form.Label className="form-label">
                                Make
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="make"
                                value={formData.make}
                                onChange={handleChange}
                                className="form-control"
                                required
                              />
                            </Col>
                            <Col xs={12} md={6}>
                              <Form.Label>Model</Form.Label>
                              <Form.Control
                                type="text"
                                name="model"
                                value={formData.model}
                                onChange={handleChange}
                                className="form-control"
                                required
                              />
                            </Col>
                          </Row>
                          <Row className="mb-2">
                            <Col xs={12} md={6}>
                              <Form.Label className="form-label">
                                <Form.Label>
                                  {formData.boatType === "Bass Boat"
                                    ? "Year"
                                    : "Motor Type"}
                                </Form.Label>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="year"
                                value={formData.year}
                                onChange={handleChange}
                                className="form-control"
                                required
                              />
                            </Col>
                            <Col xs={12} md={6}>
                              <Form.Label>
                                {formData.boatType === "Bass Boat"
                                  ? "HIN"
                                  : "Horse Power"}
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="hin"
                                value={formData.hin}
                                onChange={handleChange}
                                className="form-control"
                              />
                            </Col>
                          </Row>

                          <div>
                            {/* {formData.boatType !== "Fishing Charter" && ( */}
                            <h5 className="mt-4 mb-2">Pricings</h5>
                            {/* )} */}

                            <>
                              {/* {formData.boatType !== "Fishing Charter" && ( */}
                              <Row className="mb-3">
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>Hourly Rate</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="hourlyRate"
                                    value={pricing.hourlyRate}
                                    onChange={(e) =>
                                      setPricing({
                                        ...pricing,
                                        hourlyRate: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                    min={1}
                                    required
                                  />
                                </Form.Group>
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>Max Passengers at this price</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="maxPassengers"
                                    value={pricing.maxPassengers}
                                    onChange={(e) =>
                                      setPricing({
                                        ...pricing,
                                        maxPassengers: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                    min={1}
                                    required
                                  />
                                </Form.Group>
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>Additional passengers allowed</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="additionalPassengersAllowed"
                                    value={pricing.additionalPassengersAllowed}
                                    onChange={(e) =>
                                      setPricing({
                                        ...pricing,
                                        additionalPassengersAllowed: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                  />
                                </Form.Group>
                                <Form.Group as={Col} xs={12} md={6} className="mb-3">
                                  <Form.Label>$ Added for each additional passenger</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="additionalPassengerPercentage"
                                    value={pricing.additionalPassengerPercentage}
                                    onChange={(e) =>
                                      setPricing({
                                        ...pricing,
                                        additionalPassengerPercentage: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                  />
                                </Form.Group>
                              </Row>
                              {/* )} */}

                              {/* {formData.boatType !== "Fishing Charter" && ( */}
                              <>
                                <h6 className="mb-2 mt-3">Hourly Options</h6>
                                {pricing.hourlyOptions.map((option, index) => (
                                  <div key={index} className="mb-4 p-3 border rounded">
                                    <Row className="mb-3">
                                      <Col xs={12} md={6}>
                                        <Form.Label>Hours</Form.Label>
                                        <Form.Control
                                          type="number"
                                          name="hours"
                                          value={option.hours}
                                          onChange={(e) =>
                                            handleHourlyOptionChange(index, e)
                                          }
                                          className="form-control"
                                          required
                                        />
                                      </Col>
                                      <Col xs={12} md={6}>
                                        <Form.Label>Price</Form.Label>
                                        <Form.Control
                                          type="number"
                                          name="price"
                                          value={option.price}
                                          onChange={(e) =>
                                            handleHourlyOptionChange(index, e)
                                          }
                                          className="form-control"
                                          required
                                        />
                                      </Col>
                                    </Row>
                                    <Row className="mb-3">
                                      <Col xs={12} md={6}>
                                        <Form.Label>Max Passengers at this price</Form.Label>
                                        <Form.Control
                                          type="number"
                                          name="maxPassengers"
                                          value={option.maxPassengers}
                                          onChange={(e) =>
                                            handleHourlyOptionChange(index, e)
                                          }
                                          className="form-control"
                                          required
                                        />
                                      </Col>
                                      <Col xs={12} md={6}>
                                        <Form.Label>Additional passengers allowed</Form.Label>
                                        <Form.Control
                                          type="number"
                                          name="additionalPassengersAllowed"
                                          value={option.additionalPassengersAllowed}
                                          onChange={(e) =>
                                            handleHourlyOptionChange(index, e)
                                          }
                                          className="form-control"
                                          required
                                        />
                                      </Col>
                                    </Row>
                                    <Row className="mb-3">
                                      <Col xs={12} md={6}>
                                        <Form.Label>Amount added for each additional passenger</Form.Label>
                                        <InputGroup>
                                          <Form.Select
                                            value={option.additionalPassengerPriceType || "percentage"}
                                            onChange={(e) => {
                                              const newOptions = [...pricing.hourlyOptions];
                                              newOptions[index] = { ...newOptions[index], additionalPassengerPriceType: e.target.value };
                                              setPricing({ ...pricing, hourlyOptions: newOptions });
                                            }}
                                            style={{ maxWidth: "80px" }}
                                          >
                                            <option value="percentage">%</option>
                                            <option value="flat">$</option>
                                          </Form.Select>
                                          <Form.Control
                                            type="number"
                                            name="additionalPassengerPercentage"
                                            value={option.additionalPassengerPercentage}
                                            onChange={(e) =>
                                              handleHourlyOptionChange(index, e)
                                            }
                                            className="form-control"
                                            required
                                          />
                                        </InputGroup>
                                      </Col>
                                      <Col
                                        xs={12} md={6}
                                        className="d-flex justify-content-end align-items-end mt-2"
                                      >
                                        <Button
                                          variant="danger"
                                          onClick={() =>
                                            removeHourlyOption(index)
                                          }
                                        >
                                          Remove
                                        </Button>
                                      </Col>
                                    </Row>
                                  </div>
                                ))}
                                <Button onClick={addHourlyOption}>
                                  Add Hourly Option
                                </Button>
                              </>
                              {/* )} */}

                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-2 mt-5">Overnight Options</h6>
                                <Button
                                  variant={
                                    overnightEnabled ? "danger" : "primary"
                                  }
                                  onClick={toggleOvernight}
                                >
                                  {overnightEnabled ? "Disable" : "Enable"}{" "}
                                  Overnight Options
                                </Button>
                              </div>
                            </>

                            {pricing.dailyOptions.map((option, index) => (
                              <Row key={index}>
                                <Col xs={12} md={4}>
                                  <Form.Label>Days</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="days"
                                    disabled={!overnightEnabled}
                                    value={option.days}
                                    onChange={(e) =>
                                      handleDailyOptionChange(index, e)
                                    }
                                    className="form-control"
                                    required
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Price</Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="price"
                                    disabled={!overnightEnabled}
                                    value={option.price}
                                    onChange={(e) =>
                                      handleDailyOptionChange(index, e)
                                    }
                                    className="form-control"
                                    required
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  className="d-flex justify-content-end mt-2"
                                >
                                  <Button
                                    variant="danger"
                                    onClick={() => removeDailyOption(index)}
                                  >
                                    Remove
                                  </Button>
                                </Col>
                              </Row>
                            ))}
                            <Button onClick={addDailyOption}>
                              Add Daily Option
                            </Button>
                          </div>

                          {formData.boatType === "Bass Boat" && (
                            <>
                              <Row className="mb-2 mt-4">
                                <h5>Motor Specs</h5>
                                <Col xs={12} md={6}>
                                  <Form.Label>Motor Make</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="motorMake"
                                    value={formData.motorMake}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6}>
                                  <Form.Label>Model</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="motorModel"
                                    value={formData.motorModel}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-2">
                                <Col xs={12} md={6}>
                                  <Form.Label className="form-label">
                                    Year
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="motorYear"
                                    value={formData.motorYear}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6}>
                                  <Form.Label>Serial</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="motorSerial"
                                    value={formData.motorSerial}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-2 mt-4">
                                <h5>Trailer Specs</h5>
                                <Col xs={12} md={6}>
                                  <Form.Label className="form-label">
                                    Trailer Make
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="trailerMake"
                                    value={formData.trailerMake}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6}>
                                  <Form.Label>Dual or Single Axle</Form.Label>
                                  <Form.Control
                                    as="select" // Set the input type to "select"
                                    name="trailerAxle"
                                    value={formData.trailerAxle}
                                    onChange={handleChange}
                                    className="form-control"
                                  >
                                    <option selected value="Dual">
                                      Dual
                                    </option>
                                    <option value="Single">Single</option>
                                  </Form.Control>
                                </Col>
                              </Row>
                              <Row className="mb-2">
                                <Col xs={12} md={4}>
                                  <Form.Label>Shocks</Form.Label>
                                  <Form.Control
                                    as="select" // Set the input type to "select"
                                    name="trailerShocks"
                                    value={formData.trailerShocks}
                                    onChange={handleChange}
                                    className="form-control"
                                  >
                                    <option selected value="Yes">
                                      Yes
                                    </option>
                                    <option value="No">No</option>
                                  </Form.Control>
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Surge Brakes</Form.Label>
                                  <Form.Control
                                    as="select" // Set the input type to "select"
                                    name="trailerSurgeBrakes"
                                    value={formData.trailerSurgeBrakes}
                                    onChange={handleChange}
                                    className="form-control"
                                  >
                                    <option selected value="Yes">
                                      Yes
                                    </option>
                                    <option value="No">No</option>
                                  </Form.Control>
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label className="form-label">
                                    Year
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="trailerYear"
                                    value={formData.trailerYear}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-2">
                                <Col xs={12} md={6}>
                                  <Form.Label>VIN</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="trailerVin"
                                    value={formData.trailerVin}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={6}>
                                  <Form.Label>Spare Trailer Wheel</Form.Label>
                                  <Form.Control
                                    as="select" // Set the input type to "select"
                                    name="trailerSpareWheel"
                                    value={formData.trailerSpareWheel}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                  >
                                    <option selected value="Yes">
                                      Yes
                                    </option>
                                    <option value="No">No</option>
                                  </Form.Control>
                                </Col>
                              </Row>
                              <Row className="mb-2">
                                <Col xs={12}>
                                  <Form.Label>
                                    Are all trailer tire tread depths road legal
                                    including the spare?
                                  </Form.Label>
                                  <Form.Control
                                    as="select" // Set the input type to "select"
                                    name="trailerTireLegal"
                                    value={formData.trailerTireLegal}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                  >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                  </Form.Control>
                                </Col>
                              </Row>
                              <Row className="mb-2 mt-4">
                                <h5>Trolling Motor Specs</h5>
                                <Col xs={12} md={4}>
                                  <Form.Label className="form-label">
                                    Trolling Motor Make
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="trollingMotorMake"
                                    value={formData.trollingMotorMake}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label className="form-label">
                                    Trolling Motor Model
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="trollingMotorModel"
                                    value={formData.trollingMotorModel}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label className="form-label">
                                    Trolling Motor Year
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="trollingMotorYear"
                                    value={formData.trollingMotorYear}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-2">
                                <Col xs={12} md={6}>
                                  <Form.Label>
                                    Spare Trolling Motor Prop
                                  </Form.Label>
                                  <Form.Control
                                    as="select" // Set the input type to "select"
                                    name="trollingSpareTmProp"
                                    value={formData.trollingSpareTmProp}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                  >
                                    <option selected value="Yes">
                                      Yes
                                    </option>
                                    <option value="No">No</option>
                                  </Form.Control>
                                </Col>
                                <Col xs={12} md={6}>
                                  <Form.Label>Spare Big motor prop?</Form.Label>
                                  <Form.Control
                                    as="select" // Set the input type to "select"
                                    name="trollingBigMotorProp"
                                    value={formData.trollingBigMotorProp}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                  >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                  </Form.Control>
                                </Col>
                              </Row>
                              <Row className="mb-2 mt-4">
                                <h5>Shallow Water Anchors</h5>
                                <Col xs={12} md={6}>
                                  <Form.Label className="form-label">
                                    How many Shallow water anchors
                                  </Form.Label>
                                  <Form.Control
                                    as="select" // Set the input type to "select"
                                    name="shallowWaterAnchors"
                                    value={formData.shallowWaterAnchors}
                                    onChange={handleChange}
                                    className="form-control"
                                  >
                                    <option value="0">0</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                  </Form.Control>
                                </Col>
                                <Col xs={12} md={6}>
                                  <Form.Label className="form-label">
                                    Shallow water anchor Brand and model
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="shallowWaterAnchorBrandModel"
                                    value={
                                      formData.shallowWaterAnchorBrandModel
                                    }
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-2 mt-4">
                                <h5>FFS - Forward Facing Sonar Details</h5>
                                <Col xs={12} md={4}>
                                  <Form.Label>FFS Make</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="ffsMake"
                                    value={formData.ffsMake}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>FFS Model</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="ffsModel"
                                    value={formData.ffsModel}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>FFS Year</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min={1}
                                    name="ffsYear"
                                    value={formData.ffsYear}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 1 Make</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="graph1Make"
                                    value={formData.graph1Make}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 1 Model</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="graph1Model"
                                    value={formData.graph1Model}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 1 Year</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min={1}
                                    name="graph1Year"
                                    value={formData.graph1Year}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-2">
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 2 Make</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="graph2Make"
                                    value={formData.graph2Make}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 2 Model</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="graph2Model"
                                    value={formData.graph2Model}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 2 Year</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min={1}
                                    name="graph2Year"
                                    value={formData.graph2Year}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 3 Make</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="graph3Make"
                                    value={formData.graph3Make}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 3 Model</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="graph3Model"
                                    value={formData.graph3Model}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 3 Year</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min={1}
                                    name="graph3Year"
                                    value={formData.graph3Year}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 4 Make</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="graph4Make"
                                    value={formData.graph4Make}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 4 Model</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="graph4Model"
                                    value={formData.graph4Model}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Graph 4 Year</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min={1}
                                    name="graph4Year"
                                    value={formData.graph4Year}
                                    onChange={handleChange}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <h5 className="mt-4">Features*</h5>
                              <p>
                                Please select any significant features for your
                                watercraft.
                              </p>
                              <Row className="mb-3">
                                <Col xs={12} md={4}>
                                  <input
                                    type="text"
                                    placeholder="Feature Name"
                                    value={newCheckboxText}
                                    onChange={(e) =>
                                      setNewCheckboxText(e.target.value)
                                    }
                                    className="form-control"
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <button
                                    className="btn btn-primary"
                                    onClick={(e) => handleAddCheckbox(e)}
                                  >
                                    Add Feature
                                  </button>
                                </Col>
                              </Row>
                              <ul className="ks-cboxtags">
                                {checkboxes.map((checkbox) => (
                                  <li
                                    key={checkbox.id}
                                    className={
                                      checkbox.checked ? "ks-selected" : ""
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      id={checkbox.id}
                                      value={checkbox.label}
                                      checked={checkbox.checked}
                                      onChange={() =>
                                        handleCheckboxChange(
                                          checkbox.id,
                                          checkbox.label
                                        )
                                      }
                                    />
                                    <label htmlFor={checkbox.id}>
                                      {checkbox.label}
                                    </label>
                                  </li>
                                ))}
                              </ul>

                              <div className="mt-4 mb-3">
                                <h6 className="fw-bold">Priority Features (Drag to Reorder)</h6>
                                <p className="text-muted small">
                                  The top 3 features in this list will be displayed on your listing card. Drag and drop to reorder.
                                </p>
                                <div style={{ border: '1px solid #f0f0f0', padding: '15px', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                                  {priorityFeatures.length === 0 ? (
                                    <p className="text-muted fst-italic mb-0">Select features above to see them here.</p>
                                  ) : (
                                    <DndContext
                                      sensors={sensors}
                                      collisionDetection={closestCenter}
                                      onDragEnd={handleDragEnd}
                                    >
                                      <SortableContext
                                        items={priorityFeatures}
                                        strategy={verticalListSortingStrategy}
                                      >
                                        {priorityFeatures.map(item => (
                                          <SortableItem key={item} id={item} />
                                        ))}
                                      </SortableContext>
                                    </DndContext>
                                  )}
                                </div>
                              </div>
                              <Row className="mb-2">
                                <h5>Insurance</h5>
                                <Col xs={12} md={4}>
                                  <Form.Label className="form-label">
                                    Insurance Provider
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="insuranceProvider"
                                    value={formData.insuranceProvider}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label className="form-label">
                                    Who is the owner of the policy named insured
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="insuranceName"
                                    value={formData.insuranceName}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                  />
                                </Col>
                                <Col xs={12} md={4}>
                                  <Form.Label>Policy number</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="insuranceNumber"
                                    value={formData.insuranceNumber}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-2">
                                <Col xs={12} md={5}>
                                  <Form.Label className="form-label">
                                    Does the policy include over 300k liability
                                    coverage acceptable to most tournament
                                    trails
                                  </Form.Label>
                                  <Form.Control
                                    as="select"
                                    name="insuranceCoverage"
                                    value={formData.insuranceCoverage}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                  >
                                    <option selected value="Yes">
                                      Yes
                                    </option>
                                    <option value="No">No</option>
                                  </Form.Control>
                                </Col>
                              </Row>
                            </>
                          )}
                          <Row>
                            <Col>
                              <div className="auth mt-4">
                                <Button variant="secondary" onClick={prevStep}>
                                  Previous
                                </Button>
                                {initialData && initialData.listingId && (
                                  <Button
                                    variant="success"
                                    className="mx-2"
                                    onClick={saveFormData}
                                    disabled={loading}
                                  >
                                    {loading ? (
                                      <>
                                        <Spinner
                                          as="span"
                                          animation="border"
                                          size="sm"
                                          role="status"
                                          aria-hidden="true"
                                        />
                                        <span className="ms-1">Saving...</span>
                                      </>
                                    ) : (
                                      "Update"
                                    )}
                                  </Button>
                                )}
                                <Button
                                  className="mx-2"
                                  variant="primary"
                                  onClick={nextStep}
                                >
                                  Next
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                    </Form>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        size="lg"
        centered
        show={showLockDatesModal}
        onHide={closeLockDatesModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage Availability</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs={12} md={6} className="d-flex flex-column">
              <DateRange
                editableDateInputs={true}
                onChange={(item) => handleSelectedRange([item.selection])}
                showSelectionPreview={true}
                moveRangeOnFirstSelection={false}
                ranges={selectionRange}
                minDate={today.toDate()}
                disabledDates={disabledDates}
                onClickDate={(date) => handleDateClick(date)}
              />
              
              {/* Time Selection Section */}
              <div className="mt-3">
                <h6 className="mb-3">Optional: Lock Specific Hours</h6>
                
                {/* Current Time Range Input */}
                <Row className="mb-3">
                  <Col xs={5}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopTimePicker
                        label="Start Time"
                        value={currentStartTime}
                        onChange={(time) => setCurrentStartTime(time)}
                        sx={{ width: '100%' }}
                      />
                    </LocalizationProvider>
                  </Col>
                  <Col xs={5}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopTimePicker
                        label="End Time"
                        value={currentEndTime}
                        onChange={(time) => setCurrentEndTime(time)}
                        sx={{ width: '100%' }}
                      />
                    </LocalizationProvider>
                  </Col>
                  <Col xs={2} className="d-flex align-items-end">
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={addTimeRange}
                      disabled={!currentStartTime || !currentEndTime}
                      className="w-100"
                    >
                      <i className="material-icons">add</i>
                    </Button>
                  </Col>
                </Row>

                {/* Added Time Ranges */}
                {timeRanges.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">Time ranges to lock:</small>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={clearTimeRanges}
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="border rounded p-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                      {timeRanges.map((range) => (
                        <div key={range.id} className="d-flex justify-content-between align-items-center py-1">
                          <span className="small">{range.start} - {range.end}</span>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => removeTimeRange(range.id)}
                          >
                            <i className="material-icons" style={{ fontSize: '14px' }}>close</i>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Form.Text className="text-muted mb-3 d-block">
                  Leave time ranges empty to lock the entire selected date(s)
                </Form.Text>
              </div>
              
              <Button
                className="mb-4 mt-3"
                onClick={handleLockDates}
                variant="primary"
              >
                Lock Dates
              </Button>
            </Col>
            <Col xs={12} md={6}>
              <CreateListingDataTable
                columns={columns}
                pageSize={5}
                data={data}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          {success && <Alert severity="success">{success}</Alert>}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateListing;
